import * as XLSX from "xlsx";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Select, { components } from "react-select";

const CheckboxOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="d-flex align-items-center gap-2 py-1">
        <input
          type="checkbox"
          className="form-check-input mt-0"
          checked={props.isSelected}
          onChange={() => null}
          style={{ cursor: "pointer", width: 16, height: 16 }}
        />
        <span style={{ fontSize: "13px" }}>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CustomMenuList = (props: any) => {
  const { selectProps } = props;
  const allOptions = selectProps.options || [];
  const currentValues = props.getValue() || [];
  const isAllSelected =
    allOptions.length > 0 && currentValues.length === allOptions.length;

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    selectProps.onChange(allOptions);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    selectProps.onChange([]);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectProps.onMenuSubmit) {
      selectProps.onMenuSubmit();
    }
  };

  return (
    <components.MenuList {...props}>
      <div className="d-flex align-items-center justify-content-between p-2 px-3 border-bottom bg-light">
        <span className="small fw-semibold text-muted" style={{ fontSize: 12 }}>
          {currentValues.length} of {allOptions.length} selected
        </span>
        <div className="d-flex align-items-center gap-2">
          {!isAllSelected ? (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
              style={{ fontSize: 12 }}
              onClick={handleSelectAll}
            >
              Select All
            </button>
          ) : null}
          {currentValues.length > 0 && (
            <>
              {!isAllSelected && <span className="text-muted small">•</span>}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none text-danger fw-semibold"
                style={{ fontSize: 12 }}
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </>
          )}
        </div>
      </div>
      {props.children}
      <div className="p-2 px-3 border-top bg-light d-flex align-items-center justify-content-between">
        <span className="small text-muted" style={{ fontSize: 12 }}>
          {currentValues.length} scale(s) selected
        </span>
        <button
          type="button"
          className="btn btn-sm btn-primary px-3 py-1 fw-semibold d-flex align-items-center gap-1"
          style={{ fontSize: 12, borderRadius: 6 }}
          onClick={handleSubmit}
        >
          <i className="ti ti-check" />
          Submit
        </button>
      </div>
    </components.MenuList>
  );
};

const StepBubble: React.FC<{ index: number; current: number; label: string; onClick?: () => void }> = ({ index, current, label, onClick }) => {
  const isDone = index < current;
  const isActive = index === current;
  return (
    <div
      className="text-center position-relative px-2"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <div
        className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-bold transition-all ${isDone ? "bg-success text-white" : isActive ? "bg-danger text-white shadow" : "bg-light text-muted"}`}
        style={{
          width: "36px",
          height: "36px",
          border: isActive ? "2px solid #fff" : "none",
          fontSize: "14px",
          zIndex: 2
        }}
      >
        {isDone ? <i className="ti ti-check" style={{ fontSize: "14px" }}></i> : index}
      </div>
      <div className={`small mt-2 ${isActive ? "fw-semibold text-danger" : "text-muted"}`} style={{ fontSize: "12px" }}>
        {label}
      </div>
    </div>
  );
};

const getCurrentFinancialYear = (): string => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-indexed (Jan=1, Apr=4)
  const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  return `${startYear}-${startYear + 1}`;
};

const getPreviousFinancialYear = (): string => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-indexed (Jan=1, Apr=4)
  const currentStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  const prevStartYear = currentStartYear - 1;
  return `${prevStartYear}-${prevStartYear + 1}`;
};

const getFinancialYearOptions = (): { value: string; label: string }[] => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;

  const options: { value: string; label: string }[] = [];
  for (let i = -3; i <= 2; i++) {
    const y = startYear + i;
    const fy = `${y}-${y + 1}`;
    options.push({ value: fy, label: fy });
  }
  return options;
};

const FINANCIAL_YEAR_OPTIONS = getFinancialYearOptions();

const QUARTER_OPTIONS = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' }
];

const CADRE_OPTIONS = [
  { value: 'Non-Executive', label: 'Non-Executive' },
  { value: 'Executive', label: 'Executive' }
];

const getDynamicLastFourFYs = (targetFY?: string, promotionDueDate?: string) => {
  let cycleStartYear: number;

  if (targetFY && targetFY.includes("-")) {
    const firstPart = targetFY.split("-")[0].trim();
    cycleStartYear = parseInt(firstPart, 10);
  } else {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-indexed (Jan=1, Apr=4)
    cycleStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  }

  const dueMonth = promotionDueDate
    ? new Date(`${promotionDueDate}T00:00:00`).getMonth() + 1
    : 6;
  const includeCurrentFinancialYear = dueMonth >= 7;
  const lastYear = includeCurrentFinancialYear
    ? cycleStartYear
    : cycleStartYear - 1;

  const formatFY = (startYr: number) => `FY ${startYr}-${(startYr + 1).toString().slice(-2)}`;

  return Array.from({ length: 4 }, (_, idx) =>
    formatFY(lastYear - 3 + idx)
  );
};

const getCandidateDates = (c: any) => {
  if (!c) return { joiningDate: "-", lastPromoDate: "-", effectivePromotionDate: "N/A" };

  let joiningDate =
    c.joining_date ||
    c.joiningDate ||
    c.date_of_joining ||
    c.dateOfJoining ||
    c.doj ||
    c.doj_date ||
    c.dojDate ||
    "";

  let lastPromoDate =
    c.last_promotion_date ||
    c.lastPromotionDate ||
    c.last_promo_date ||
    c.lastPromoDate ||
    c.last_promotion ||
    c.lastPromotion ||
    c.promo_date ||
    c.promoDate ||
    "";

  let effectivePromotionDate =
    c.adjustedPromotionDate ||
    c.adjusted_promotion_date ||
    c.effective_promotion_date ||
    c.effectivePromotionDate ||
    c.effective_promo_date ||
    c.effectivePromoDate ||
    c.effectiveDate ||
    c.effective_date ||
    "";

  const combined =
    c.joining_or_last_promo_date ||
    c.joiningOrLastPromoDate ||
    c.joining_last_promo_date ||
    c.joiningLastPromoDate ||
    "";

  if (combined && typeof combined === "string") {
    if (combined.includes("/")) {
      const parts = combined.split("/").map((s) => s.trim());
      if (parts.length >= 2) {
        if (!joiningDate) joiningDate = parts[0];
        if (!lastPromoDate) lastPromoDate = parts[1];
      } else if (parts.length === 1 && !joiningDate) {
        joiningDate = parts[0];
      }
    } else if (combined.includes(",")) {
      const parts = combined.split(",").map((s) => s.trim());
      if (parts.length >= 2) {
        if (!joiningDate) joiningDate = parts[0];
        if (!lastPromoDate) lastPromoDate = parts[1];
      } else if (parts.length === 1 && !joiningDate) {
        joiningDate = parts[0];
      }
    } else if (combined.includes(" - ")) {
      const parts = combined.split(" - ").map((s) => s.trim());
      if (parts.length >= 2) {
        if (!joiningDate) joiningDate = parts[0];
        if (!lastPromoDate) lastPromoDate = parts[1];
      } else if (parts.length === 1 && !joiningDate) {
        joiningDate = parts[0];
      }
    } else if (!joiningDate) {
      joiningDate = combined;
    }
  }

  const formatJoiningDate = (val: any) => {
    if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "-";
    if (typeof val === "string" && val.includes("T")) {
      return val.split("T")[0];
    }
    return String(val);
  };

  const formatLastPromoDate = (val: any) => {
    if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "No Data Found";
    if (typeof val === "string" && val.includes("T")) {
      return val.split("T")[0];
    }
    return String(val);
  };

  const formatEffectivePromoDate = (val: any) => {
    if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "N/A";
    if (typeof val === "string" && val.includes("T")) {
      return val.split("T")[0];
    }
    return String(val);
  };

  return {
    joiningDate: formatJoiningDate(joiningDate),
    lastPromoDate: formatLastPromoDate(lastPromoDate),
    effectivePromotionDate: formatEffectivePromoDate(effectivePromotionDate)
  };
};

const getCategory = (c: any) => {
  if (!c) return "-";
  const cat =
    c.category ||
    c.emp_category ||
    c.empCategory ||
    c.caste_category ||
    c.casteCategory ||
    c.cast_category ||
    c.castCategory ||
    c.caste ||
    c.social_category ||
    c.socialCategory ||
    "";
  if (!cat || cat === "N/A" || cat === "null" || cat === "undefined") return "-";
  return String(cat);
};

const formatDateDMY = (dateVal: any): string => {
  if (!dateVal || dateVal === "N/A" || dateVal === "null" || dateVal === "undefined" || dateVal === "—" || dateVal === "-") {
    return "—";
  }
  const str = String(dateVal).trim();
  if (!str) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-");
    return `${d}-${m}-${y}`;
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    const datePart = str.split("T")[0];
    const [y, m, d] = datePart.split("-");
    return `${d}-${m}-${y}`;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    return str;
  }
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return str || "—";
};

const getDarDetails = (c: any) => {
  if (!c) {
    return {
      statusText: "No D&AR Record",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: "—",
      chargeSheetIssuedDate: "—",
      penaltyDescription: "—",
      dateOfAction: "—",
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  const rawStatus = (c.darStatus ?? c.dar_status ?? "").toString().trim();
  const caseNoRaw = (c.caseNo ?? c.case_no ?? "").toString().trim();
  const chargeSheetDateRaw = c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date;
  const penaltyDescRaw = (c.penaltyDescription ?? c.penalty_description ?? "").toString().trim();
  const dateOfActionRaw = c.dateOfAction ?? c.date_of_action;
  const penaltyStartRaw = c.penaltyStartDate ?? c.penalty_start_date;
  const penaltyEndRaw = c.penaltyEndDate ?? c.penalty_end_date;

  const formatVal = (val: string) => (!val || val === "null" || val === "undefined" || val === "N/A" ? "—" : val);

  const statusLower = rawStatus.toLowerCase();

  if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared") {
    return {
      statusText: "DAR CLEAR",
      badgeClass: "bg-success text-white",
      badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: "—",
      chargeSheetIssuedDate: "—",
      penaltyDescription: "—",
      dateOfAction: "—",
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
    return {
      statusText: "ChargeSheet Issued",
      badgeClass: "bg-warning text-dark",
      badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: "—",
      dateOfAction: "—",
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
    return {
      statusText: "Minor Penalty",
      badgeClass: "bg-warning text-dark",
      badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: "—",
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
    return {
      statusText: "Major Penalty",
      badgeClass: "bg-danger text-white",
      badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: "—",
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: "—",
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  if (!rawStatus || rawStatus === "true" || rawStatus === "false") {
    return {
      statusText: "No D&AR Record",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: "—",
      chargeSheetIssuedDate: "—",
      penaltyDescription: "—",
      dateOfAction: "—",
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  return {
    statusText: rawStatus,
    badgeClass: "bg-secondary text-white",
    badgeStyle: { backgroundColor: "#475569", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
    caseNo: formatVal(caseNoRaw),
    chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
    penaltyDescription: formatVal(penaltyDescRaw),
    dateOfAction: formatDateDMY(dateOfActionRaw),
    penaltyStartDate: formatDateDMY(penaltyStartRaw),
    penaltyEndDate: formatDateDMY(penaltyEndRaw)
  };
};

const formatAparScore = (val: any): string => {
  if (val === undefined || val === null || val === "" || val === "—") return "-";
  const str = String(val).trim();
  const num = parseFloat(str);
  if (!isNaN(num) && num === 0) return "-";
  if (str === "0" || str === "0.0" || str === "0.00") return "-";
  return str;
};

const getCandidateAparScores = (c: any, evaluatedFYs: string[]): { scores: string[]; total: string; remarks: string } => {
  if (!c) return { scores: ["-", "-", "-", "-"], total: "-", remarks: "-" };

  let aparObj: any = {};
  const rawObj = c.apar_scores ?? c.aparScores ?? c.scores ?? c.year_scores ?? c.yearScores;
  if (typeof rawObj === "string") {
    try {
      aparObj = JSON.parse(rawObj);
    } catch (e) {
      aparObj = {};
    }
  } else if (rawObj && typeof rawObj === "object") {
    aparObj = rawObj;
  }

  const scores = evaluatedFYs.map((fyLabel, idx) => {
    if (aparObj[fyLabel] !== undefined && aparObj[fyLabel] !== null) return formatAparScore(aparObj[fyLabel]);

    const cleanFY = fyLabel.replace(/^FY\s*/i, "").trim();
    if (aparObj[cleanFY] !== undefined && aparObj[cleanFY] !== null) return formatAparScore(aparObj[cleanFY]);

    const fullYearFY = cleanFY.replace(/-(\d{2})$/, (_, p1) => `-20${p1}`);
    if (aparObj[fullYearFY] !== undefined && aparObj[fullYearFY] !== null) return formatAparScore(aparObj[fullYearFY]);

    if (c[fyLabel] !== undefined && c[fyLabel] !== null) return formatAparScore(c[fyLabel]);
    if (c[cleanFY] !== undefined && c[cleanFY] !== null) return formatAparScore(c[cleanFY]);
    if (c[fullYearFY] !== undefined && c[fullYearFY] !== null) return formatAparScore(c[fullYearFY]);

    const posKeys = [
      `apar${idx + 1}`,
      `apar_${idx + 1}`,
      `aparScore${idx + 1}`,
      `apar_score_${idx + 1}`,
      `fy${idx + 1}`,
      `fy${idx + 1}_score`,
      `fy_${idx + 1}`
    ];
    for (const key of posKeys) {
      if (c[key] !== undefined && c[key] !== null) return formatAparScore(c[key]);
      if (aparObj[key] !== undefined && aparObj[key] !== null) return formatAparScore(aparObj[key]);
    }

    return "-";
  });

  const total = formatAparScore(
    c.apar_total_score ??
    c.aparTotalScore ??
    c.total_apar_score ??
    c.totalAparScore ??
    c.apar_total ??
    c.aparTotal
  );

  const remarks =
    c.aparRemarks ??
    c.apar_remarks ??
    c.aparRemark ??
    c.apar_remark ??
    "—";

  return { scores, total: String(total), remarks: String(remarks) };
};

const getCandidateAparRemarks = (c: any): string => {
  if (!c) return "—";
  const r =
    c.aparRemarks ??
    c.apar_remarks ??
    c.aparRemark ??
    c.apar_remark ??
    c.apar_remark;
  if (r === undefined || r === null || r === "") return "—";
  return String(r);
};

const candidateMatchesSearch = (c: any, query: string) => {
  const value = query.trim().toLowerCase();
  if (!value) return true;
  return String(c.emp_no || c.empId || c.empNo || "").toLowerCase().includes(value)
    || String(c.emp_name || c.empName || c.employeeName || "").toLowerCase().includes(value);
};

const checkStatus = (c: any, stage: string) => String(
  c.checks?.[stage] || c.checkResults?.[stage]?.status || c.check_results?.[stage]?.status
  || c[`${stage}_status`] || c[`${stage}Status`] || "PENDING"
).toUpperCase();

const servicePassed = (c: any) => checkStatus(c, "service") === "PASSED";
const serviceShortfall = (c: any) => checkStatus(c, "service") === "FAILED";
const aparPassed = (c: any) => checkStatus(c, "apar") === "PASSED"
  || c.apar_suitable === true || c.aparSuitable === true || c.apar_evaluation?.isSuitable === true;
const aparFailed = (c: any) => checkStatus(c, "apar") === "FAILED"
  || c.apar_suitable === false || c.aparSuitable === false || c.apar_evaluation?.isSuitable === false;
const darClear = (c: any) => checkStatus(c, "dar") === "PASSED" || c.darCleared === true || c.dar_cleared === true;
const darNotClear = (c: any) => checkStatus(c, "dar") === "FAILED"
  || (checkStatus(c, "dar") !== "PENDING" && (c.darCleared === false || c.dar_cleared === false));
const vigilanceSuitable = (c: any) => checkStatus(c, "vigilance") === "PASSED"
  && checkStatus(c, "criminal") === "PASSED";

const NewPromotionCycle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryCycleId = queryParams.get("cycleId");
  const queryStep = queryParams.get("step");

  const [step, setStep] = useState<number>(() => {
    if (queryStep) {
      const parsed = Number(queryStep);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 6) {
        return parsed;
      }
    }
    return 1;
  });

  // Step 1 state
  const [cycleName, setCycleName] = useState("");
  const [cadre, setCadre] = useState("Non-Executive");
  const [cycleStartDate, setCycleStartDate] = useState("");
  const promotionDateFrom = cycleStartDate;
  const promotionDateTo = cycleStartDate;
  const [selectedGrades, setSelectedGrades] = useState<any[]>([]);
  const [gradesMenuOpen, setGradesMenuOpen] = useState(false);
  const [financialYear, setFinancialYear] = useState<string>(getPreviousFinancialYear());
  const [employeeRange, setEmployeeRange] = useState("");
  const [cycleId, setCycleId] = useState<number | null>(() => {
    if (queryCycleId) {
      const parsed = Number(queryCycleId);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  });
  const [initiatedCycle, setInitiatedCycle] = useState<any>(null);
  const [initiateMessage, setInitiateMessage] = useState("");

  // Keep URL parameters in sync with current step and cycleId so page refresh stays on the same step
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    let needUpdate = false;

    if (step && currentParams.get("step") !== String(step)) {
      currentParams.set("step", String(step));
      needUpdate = true;
    }

    if (cycleId && currentParams.get("cycleId") !== String(cycleId)) {
      currentParams.set("cycleId", String(cycleId));
      needUpdate = true;
    }

    if (needUpdate) {
      navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
    }
  }, [step, cycleId, location.pathname, location.search, navigate]);

  // Sync step from URL if user navigates using browser back/forward buttons
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sParam = params.get("step");
    if (sParam) {
      const parsed = Number(sParam);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 6 && parsed !== step) {
        setStep(parsed);
      }
    }
  }, [location.search]);

  // Step 2 state
  const [requiredServiceYears, setRequiredServiceYears] = useState<number | "">(5);
  const [excludeLWP, setExcludeLWP] = useState(true);
  const [excludeSuspension, setExcludeSuspension] = useState(true);

  // Step 3 state
  const [generalRules, setGeneralRules] = useState<{ "4": number; "3": number; "2": number }>({
    "4": 32,
    "3": 24,
    "2": 16
  });
  const [scstRules, setScstRules] = useState<{ "4": number; "3": number; "2": number }>({
    "4": 30,
    "3": 22.5,
    "2": 15
  });
  const generalMarks = generalRules["4"];
  const scstMarks = scstRules["4"];
  const [isAparUploaded, setIsAparUploaded] = useState(false);

  const validateAparBenchmarks = () => {
    const values = [...Object.values(generalRules), ...Object.values(scstRules)];
    if (values.some(value => !Number.isFinite(value) || value <= 0 || Number(value.toFixed(3)) !== value)) {
      toast.error("Enter positive APAR benchmarks with no more than 3 decimal places.");
      return false;
    }
    return true;
  };

  const runWizardStep = async (
    text: string,
    action: () => Promise<void>
  ) => {
    setModalText(text);
    setProgressPercent(0);
    setProgressStatus("Processing...");
    setShowProgressModal(true);

    let progress = 0;
    const intervalId = setInterval(() => {
      progress += 1;
      if (progress >= 95) {
        progress = 95;
        clearInterval(intervalId);
      }
      setProgressPercent(progress);
    }, 800);

    try {
      await action();
      clearInterval(intervalId);
      setProgressPercent(100);
      setProgressStatus("Completed!");
      await new Promise((resolve) => setTimeout(resolve, 800));
    } catch (err) {
      clearInterval(intervalId);
      throw err;
    } finally {
      setShowProgressModal(false);
    }
  };

  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const [gradesMaster, setGradesMaster] = useState<any[]>([]);
  const [isInitiating, setIsInitiating] = useState(false);

  const [candidates, setCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQueryStep2, setSearchQueryStep2] = useState("");
  const [statusFilterStep2, setStatusFilterStep2] = useState("ALL");

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressStatus, setProgressStatus] = useState("Processing...");
  const [modalText, setModalText] = useState("");
  const [passedServiceCandidates, setPassedServiceCandidates] = useState<any[]>([]);
  const [searchQueryStep3, setSearchQueryStep3] = useState("");
  const [statusFilterStep3, setStatusFilterStep3] = useState("ALL");
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [candidates]);

  useEffect(() => {
    const fetchCandidates = async () => {
      if (!cycleId) return;
      setLoadingCandidates(true);
      try {
        const response = await Api.get(API_URLS.PROMOTION.LIST, [{ cycleId }], true);
        const resData = response?.data || response;
        if (Array.isArray(resData)) {
          setCandidates(resData);
        } else if (resData && Array.isArray(resData.data)) {
          setCandidates(resData.data);
        } else {
          setCandidates([]);
        }
      } catch (error) {
        console.error("Failed to fetch candidates:", error);
        toast.error("Failed to fetch candidates list");
      } finally {
        setLoadingCandidates(false);
      }
    };
    if (step === 2) {
      fetchCandidates();
    }
  }, [step, cycleId]);
  const [currentPageStep3, setCurrentPageStep3] = useState(1);
  const [pageSizeStep3, setPageSizeStep3] = useState(10);
  const [loadingPassedCandidates, setLoadingPassedCandidates] = useState(false);

  useEffect(() => {
    setCurrentPageStep3(1);
  }, [passedServiceCandidates]);

  useEffect(() => {
    const fetchPassedCandidates = async () => {
      if (!cycleId) return;
      setLoadingPassedCandidates(true);
      try {
        const response = await Api.get(API_URLS.PROMOTION.LIST, [{ cycleId }], true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;
        if (Array.isArray(innerData)) {
          setPassedServiceCandidates(innerData);
        } else if (innerData && Array.isArray(innerData.data)) {
          setPassedServiceCandidates(innerData.data);
        } else {
          setPassedServiceCandidates([]);
        }
      } catch (error) {
        console.error("Failed to fetch passed candidates:", error);
        toast.error("Failed to fetch passed candidates list");
      } finally {
        setLoadingPassedCandidates(false);
      }
    };
    if (step === 3) {
      fetchPassedCandidates();
    }
  }, [step, cycleId]);
  const [passedAparCandidates, setPassedAparCandidates] = useState<any[]>([]);
  const [loadingPassedAparCandidates, setLoadingPassedAparCandidates] = useState(false);
  const [currentPageStep4, setCurrentPageStep4] = useState(1);
  const [pageSizeStep4, setPageSizeStep4] = useState(10);
  const [searchQueryStep4, setSearchQueryStep4] = useState("");
  const [statusFilterStep4, setStatusFilterStep4] = useState("ALL");

  useEffect(() => {
    setCurrentPageStep4(1);
  }, [passedAparCandidates]);

  useEffect(() => {
    const fetchPassedAparCandidates = async () => {
      if (!cycleId) return;
      setLoadingPassedAparCandidates(true);
      try {
        const response = await Api.post(API_URLS.PROMOTION.ELIGIBLE_APAR_LIST, { cycleId }, true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;
        if (Array.isArray(innerData)) {
          setPassedAparCandidates(innerData);
        } else if (innerData && Array.isArray(innerData.data)) {
          setPassedAparCandidates(innerData.data);
        } else {
          setPassedAparCandidates([]);
        }
      } catch (error) {
        console.error("Failed to fetch passed APAR candidates:", error);
        toast.error("Failed to fetch passed APAR candidates list");
      } finally {
        setLoadingPassedAparCandidates(false);
      }
    };
    if (step === 4) {
      fetchPassedAparCandidates();
    }
  }, [step, cycleId]);

  // Step 5: D&AR passed candidates & Vigilance clearance inputs
  const [passedDarCandidates, setPassedDarCandidates] = useState<any[]>([]);
  const [loadingPassedDarCandidates, setLoadingPassedDarCandidates] = useState(false);
  const [currentPageStep5, setCurrentPageStep5] = useState(1);
  const [pageSizeStep5, setPageSizeStep5] = useState(10);
  const [searchQueryStep5, setSearchQueryStep5] = useState("");
  const [statusFilterStep5, setStatusFilterStep5] = useState("ALL");
  const [selectedDarCandidate, setSelectedDarCandidate] = useState<any | null>(null);

  const [vigilanceTypeMap, setVigilanceTypeMap] = useState<{ [empId: string]: string }>({});
  const [criminalCaseMap, setCriminalCaseMap] = useState<{ [empId: string]: string }>({});
  const [vigilanceRemarksMap, setVigilanceRemarksMap] = useState<{ [empId: string]: string }>({});
  const [vigilanceErrorsMap, setVigilanceErrorsMap] = useState<{ [empId: string]: string }>({});

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedDarCandidate) {
        setSelectedDarCandidate(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDarCandidate]);

  const validateAndBuildVigilancePayload = () => {
    const candidatesPayload: any[] = [];
    const errors: { [empId: string]: string } = {};
    let firstInvalidEmpId: string | null = null;

    for (const c of passedDarCandidates) {
      const empIdKey = String(c.emp_no || c.empId || c.empNo || "");
      const vigType = vigilanceTypeMap[empIdKey] ?? c.vigilanceType ?? c.vigilance_type ?? "No Vigilance Case";
      const crimCaseRaw = criminalCaseMap[empIdKey] ?? (c.criminalCase ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : "No") : (c.criminal_case ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : "No") : "No"));
      const crimCaseApi = (crimCaseRaw === "Yes" || crimCaseRaw === "yes") ? "yes" : "no";

      let remarks = vigilanceRemarksMap[empIdKey] ?? c.vigilanceRemarks ?? c.vigilance_remarks ?? "";
      if (!remarks && vigType === "No Vigilance Case") {
        remarks = "No vigilance or criminal case pending";
      }

      const trimmedRemarks = remarks.trim();

      if ((vigType === "Vigilance Initiated" || vigType === "Penalty Awarded") && !trimmedRemarks) {
        errors[empIdKey] = `Vigilance Remarks are mandatory when Vigilance Type is '${vigType}'.`;
        if (!firstInvalidEmpId) firstInvalidEmpId = empIdKey;
      }

      candidatesPayload.push({
        empId: empIdKey,
        vigilanceType: vigType,
        criminalCase: crimCaseApi,
        vigilanceRemarks: trimmedRemarks || "No vigilance or criminal case pending"
      });
    }

    setVigilanceErrorsMap(errors);

    if (firstInvalidEmpId) {
      return { valid: false, errors, payload: null, firstInvalidEmpId };
    }

    return {
      valid: true,
      errors: {},
      payload: {
        cycleId: Number(cycleId || new URLSearchParams(location.search).get("cycleId") || 1),
        candidates: candidatesPayload
      },
      firstInvalidEmpId: null
    };
  };

  useEffect(() => {
    setCurrentPageStep5(1);
  }, [passedDarCandidates]);

  useEffect(() => {
    const fetchPassedDarCandidates = async () => {
      if (!cycleId) return;
      setLoadingPassedDarCandidates(true);
      try {
        const response = await Api.post(API_URLS.PROMOTION.ELIGIBLE_DAR_LIST, { cycleId: Number(cycleId) }, true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;
        if (Array.isArray(innerData)) {
          setPassedDarCandidates(innerData);
        } else if (innerData && Array.isArray(innerData.data)) {
          setPassedDarCandidates(innerData.data);
        } else {
          setPassedDarCandidates([]);
        }
      } catch (error) {
        console.error("Failed to fetch passed DAR candidates:", error);
        toast.error("Failed to fetch passed DAR candidates list");
      } finally {
        setLoadingPassedDarCandidates(false);
      }
    };
    if (step === 5) {
      fetchPassedDarCandidates();
    }
  }, [step, cycleId]);

  // Step 6: Eligible DPC candidates
  const [eligibleDpcCandidates, setEligibleDpcCandidates] = useState<any[]>([]);
  const [loadingEligibleDpc, setLoadingEligibleDpc] = useState(false);
  const [currentPageStep6, setCurrentPageStep6] = useState(1);
  const [pageSizeStep6, setPageSizeStep6] = useState(10);
  const [searchQueryStep6, setSearchQueryStep6] = useState("");
  const [statusFilterStep6, setStatusFilterStep6] = useState("ALL");
  useEffect(() => {
    setSearchQueryStep2(""); setStatusFilterStep2("ALL"); setCurrentPage(1);
    setSearchQueryStep3(""); setStatusFilterStep3("ALL"); setCurrentPageStep3(1);
    setSearchQueryStep4(""); setStatusFilterStep4("ALL"); setCurrentPageStep4(1);
    setSearchQueryStep5(""); setStatusFilterStep5("ALL"); setCurrentPageStep5(1);
    setSearchQueryStep6(""); setStatusFilterStep6("ALL"); setCurrentPageStep6(1);
  }, [cycleId]);
  useEffect(() => {
    setCurrentPageStep6(1);
  }, [eligibleDpcCandidates]);

  useEffect(() => {
    const fetchEligibleDpcCandidates = async () => {
      if (!cycleId) return;
      setLoadingEligibleDpc(true);
      try {
        const payload = {
          Request: {
            data: {
              cycleId: Number(cycleId)
            }
          }
        };
        const response = await Api.post(API_URLS.PROMOTION.ELIGIBLE_VIGILANCE_LIST, payload, true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;

        let candidateList: any[] = [];
        if (Array.isArray(innerData)) {
          candidateList = innerData;
        } else if (innerData && Array.isArray(innerData.eligibleListDpc)) {
          candidateList = innerData.eligibleListDpc;
        } else if (innerData && Array.isArray(innerData.eligibleList)) {
          candidateList = innerData.eligibleList;
        } else if (innerData && Array.isArray(innerData.data)) {
          candidateList = innerData.data;
        } else if (resData && Array.isArray(resData.eligibleListDpc)) {
          candidateList = resData.eligibleListDpc;
        } else if (resData && Array.isArray(resData.eligibleList)) {
          candidateList = resData.eligibleList;
        } else if (resData && Array.isArray(resData.data)) {
          candidateList = resData.data;
        }

        const hasClassification = candidateList.some((c: any) => c && (c.listClassification !== undefined || c.list_classification !== undefined));
        if (hasClassification) {
          candidateList = candidateList.filter((c: any) => 
            c.listClassification === 'ELIGIBLE_DPC' || 
            c.list_classification === 'ELIGIBLE_DPC' || 
            c.listClassification === 'ELIGIBLE' ||
            c.list_classification === 'ELIGIBLE'
          );
        }

        setEligibleDpcCandidates(candidateList);
      } catch (error) {
        console.error("Failed to fetch eligible DPC candidates:", error);
        toast.error("Failed to fetch eligible DPC candidates list");
      } finally {
        setLoadingEligibleDpc(false);
      }
    };
    if (step === 6) {
      fetchEligibleDpcCandidates();
    }
  }, [step, cycleId]);

  useEffect(() => {
    const fetchGradesAndActiveCycle = async () => {
      let masterGrades: any[] = [];
      try {
        const response = await Api.get(API_URLS.PROMOTION.PAY_SCALE_GRADE_MASTER, [{ employeeType: "NON-EXECUTIVE" }], true);
        if (response && response.data && response.data.data) {
          masterGrades = response.data.data;
          setGradesMaster(response.data.data);
        } else if (response && response.data) {
          masterGrades = response.data;
          setGradesMaster(response.data);
        }
      } catch (e) {
        console.error("Failed to fetch grades master", e);
      }

      // Check for active cycle and redirect if one exists (only when resuming via queryCycleId)
      if (queryCycleId) {
        try {
          const response = await Api.get(API_URLS.PROMOTION.CYCLES, null, true);
          const resData = response?.data || response;
          const cyclesList = Array.isArray(resData) ? resData : (resData?.data || []);
          
          // Find the cycle matching queryCycleId
          const active = cyclesList.find((c: any) => String(c.id) === String(queryCycleId));
          if (active) {
            setCycleId(active.id);
            setInitiatedCycle(active);
            setFinancialYear(active.financialYear || getPreviousFinancialYear());
            setCadre(active.cadre || "Non-Executive");
            setCycleStartDate(active.cycleStartDate || active.promotionDateFrom || active.promotionDateTo || "");
            setCycleName(active.cycleName || "");
            if (active.aparBenchmarks?.general) {
              const rules = active.aparBenchmarks.general;
              setGeneralRules({ "4": rules.fourYears, "3": rules.threeYears, "2": rules.twoYears });
            }
            if (active.aparBenchmarks?.scst) {
              const rules = active.aparBenchmarks.scst;
              setScstRules({ "4": rules.fourYears, "3": rules.threeYears, "2": rules.twoYears });
            }

            
            // Parse and match grades
            let parsedGrades: string[] = [];
            if (active.grades) {
              try {
                parsedGrades = typeof active.grades === 'string' ? JSON.parse(active.grades) : active.grades;
              } catch (e) {
                console.error("Failed to parse grades for active cycle:", e);
              }
            }
            if (active.employeeRange || active.employee_range) {
              setEmployeeRange(active.employeeRange || active.employee_range);
            }
            if (parsedGrades.length > 0) {
              const mapped = parsedGrades.map(gCode => {
                const matched = masterGrades.find((m: any) => m.gradeCode === gCode || m.gradeFullName === gCode || m.empPayscale === gCode);
                return {
                  value: gCode,
                  label: matched ? (matched.gradeFullName || matched.gradeCode) : gCode
                };
              });
              setSelectedGrades(mapped);
            }
            
            // Redirect to appropriate step based on cycle status if step is not explicitly provided in URL
            if (!queryStep) {
              if (active.status === 'INITIATED') {
                setStep(2);
              } else if (active.status === 'ELIGIBILITY_CALCULATED') {
                setStep(3);
              } else if (active.status === 'APAR_CALCULATED') {
                setStep(4);
              } else if (active.status === 'DAR_CALCULATED') {
                setStep(5);
              } else if (active.status === 'VIGILANCE_CLEARED') {
                setStep(6);
              } else if (active.status === 'DPC_REVIEW') {
                navigate(`${all_routes.PostPromotion.DpcReview}?cycleId=${active.id}`, { state: { cycleId: active.id } });
              } else if (active.status === 'COMPETENT_AUTHORITY_APPROVAL') {
                navigate(`${all_routes.PostPromotion.DpcApprovalList}?cycleId=${active.id}`, { state: { cycleId: active.id } });
              } else if (active.status === 'COMPLETED') {
                setStep(6);
              }
            }
          }
        } catch (e) {
          console.error("Failed to fetch active cycle details:", e);
        }
      }
    };

    fetchGradesAndActiveCycle();
  }, []);

  const goNext = async () => {
    if (step === 1) {
      if (selectedGrades.length === 0) {
        toast.error("Please select at least one Pay Scale");
        return;
      }
      if (!cycleStartDate) {
        toast.error("Please select Promotion Due On");
        return;
      }
      setIsInitiating(true);
      try {
        await runWizardStep("Initiating cycle in progress...", async () => {
          const payload: any = {
            financialYear: financialYear,
            cadre: cadre,
            cycleStartDate: cycleStartDate || "",
            promotionDateFrom: cycleStartDate || "",
            promotionDateTo: cycleStartDate || "",
            grades: selectedGrades.map((g: any) => g.value),
            employeePayScaleGrade: selectedGrades.map((g: any) => g.value),
            employeeRange: employeeRange || "all"
          };

          const response = await Api.post(API_URLS.PROMOTION.INITIATE, payload, true);
          console.log("INITIATE Response:", response);

          // Extract cycle details and message robustly
          const resData = response?.data || response;
          const innerData = resData?.data || resData;
          if (innerData?.cycle) {
            setInitiatedCycle(innerData.cycle);
          } else if (resData?.cycle) {
            setInitiatedCycle(resData.cycle);
          }
          if (innerData?.message) {
            setInitiateMessage(innerData.message);
          } else if (resData?.message) {
            setInitiateMessage(resData.message);
          }

          let newCycleId: any = null;
          if (response) {
            newCycleId = response?.data?.data?.cycle?.id
              || response?.data?.cycle?.id
              || response?.data?.data?.id
              || response?.data?.id
              || response?.id
              || response?.data?.data?.cycleId
              || response?.data?.cycleId
              || response?.cycleId;
          }
          if (newCycleId) {
            setCycleId(newCycleId);
            // Save initiated cycle details to localStorage
            const payScaleStr = selectedGrades.map((g: any) => g.label || g.value).join(", ");
            const newCycle = {
              id: newCycleId,
              name: `Promotion - ${payScaleStr} - ${financialYear}`,
              financialYear: financialYear,
              cadre: cadre,
              cycleStartDate: cycleStartDate || "",
              promotionDateFrom: cycleStartDate || "",
              promotionDateTo: cycleStartDate || "",
              grades: selectedGrades.map((g: any) => g.value),
              employeePayScaleGrade: selectedGrades.map((g: any) => g.value),
              employeeRange: employeeRange || "all"
            };
            try {
              const storedCycles = JSON.parse(localStorage.getItem("promotion_cycles") || "[]");
              if (!storedCycles.some((c: any) => c.id === newCycleId)) {
                storedCycles.push(newCycle);
                localStorage.setItem("promotion_cycles", JSON.stringify(storedCycles));
              }
            } catch (e) {
              console.error("Failed to save initiated cycle to localStorage", e);
            }
          } else {
            console.error("Cycle ID not found in INITIATE response!", response);
          }
        });
        setStep(2);
      } catch (error: any) {
        toast.error("Failed to initiate promotion cycle: " + Api.getErrorMessage(error));
      } finally {
        setIsInitiating(false);
      }
    } else if (step === 2) {
      if (!cycleId) {
        toast.error("Cycle ID is missing. Please restart initiation.");
        return;
      }
      try {
        await runWizardStep("Service Calculation In Progress...", async () => {
          const payload = {
            cycleId: cycleId,
            page: 1,
            limit: 100000
          };
          const response = await Api.post(API_URLS.PROMOTION.CALCULATE_SERVICE, payload, true);
          const resData = response?.data || response;
          const innerData = resData?.data || resData;
          if (Array.isArray(innerData)) {
            const filtered = innerData.filter((c: any) => c.listClassification !== 'LIST_A');
            setPassedServiceCandidates(filtered);
          } else if (innerData?.data && Array.isArray(innerData.data)) {
            const filtered = innerData.data.filter((c: any) => c.listClassification !== 'LIST_A');
            setPassedServiceCandidates(filtered);
          }
          toast.success("Service periods and LWP calculated successfully!");
          setStep(3);
        });
      } catch (error: any) {
        console.error("Service calculation failed:", error);
        toast.error("Service calculation failed: " + Api.getErrorMessage(error));
      }
    } else if (step === 3) {
      if (!validateAparBenchmarks()) return;
      if (!cycleId) {
        toast.error("Cycle ID is missing. Please restart initiation.");
        return;
      }
      try {
        await runWizardStep("Calculating APAR scores, please wait...", async () => {
          const payload = {
            cycleId: cycleId,
            aparRules: { general: generalRules, scst: scstRules },
            page: 1,
            limit: 100000
          };
          await Api.post(API_URLS.PROMOTION.APAR_SCORES, payload, true);
          toast.success("APAR scores aggregated successfully!");
          setIsAparUploaded(true);
          setStep(4);
        });
      } catch (error: any) {
        console.error("APAR evaluation failed:", error);
        toast.error("APAR evaluation failed: " + Api.getErrorMessage(error));
      }
    } else if (step === 4) {
      if (!cycleId) {
        toast.error("Cycle ID is missing. Please restart initiation.");
        return;
      }
      try {
        await runWizardStep("Auditing D&AR Clearance...", async () => {
          const payload = {
            cycleId: cycleId,
            page: 1,
            limit: 100000
          };
          await Api.post(API_URLS.PROMOTION.DAR_CLEARANCE, payload, true);
          toast.success("D&AR clearance checks completed!");
          setStep(5);
        });
      } catch (error: any) {
        console.error("D&AR audit failed:", error);
        toast.error("D&AR audit failed: " + Api.getErrorMessage(error));
      }
    } else if (step === 5) {
      if (!cycleId) {
        toast.error("Cycle ID is missing. Please restart initiation.");
        return;
      }

      const candidateList = passedDarCandidates || [];

      // Validate mandatory Vigilance Remarks for all candidates
      let hasValidationError = false;
      const newErrors: Record<string, string> = {};
      let firstErrorEmpName = "";

      for (let idx = 0; idx < candidateList.length; idx++) {
        const c = candidateList[idx];
        const empIdKey = String(c.emp_no || c.empId || c.empNo || idx);
        const vType = vigilanceTypeMap[empIdKey] ?? c.vigilanceType ?? c.vigilance_type ?? "No Vigilance Case";
        const vRemarks = (vigilanceRemarksMap[empIdKey] ?? c.vigilanceRemarks ?? c.vigilance_remarks ?? (vType === "No Vigilance Case" ? "No vigilance or criminal case pending" : "")).trim();

        if ((vType === "Vigilance Initiated" || vType === "Penalty Awarded") && !vRemarks) {
          hasValidationError = true;
          const empName = c.emp_name || c.empName || c.employeeName || empIdKey;
          newErrors[empIdKey] = `Vigilance Remarks are mandatory for '${vType}'.`;
          if (!firstErrorEmpName) {
            firstErrorEmpName = `${empName} (${empIdKey})`;
          }
        }
      }

      if (hasValidationError) {
        setVigilanceErrorsMap((prev) => ({ ...prev, ...newErrors }));
        toast.error(`Please enter mandatory Vigilance Remarks for candidate: ${firstErrorEmpName}`);
        return;
      }

      try {
        await runWizardStep("Submitting Vigilance Clearance Details...", async () => {
          const payloadCandidates = candidateList.map((c: any, idx: number) => {
            const empIdKey = String(c.emp_no || c.empId || c.empNo || idx);
            const empIdVal = String(c.empId || c.emp_no || c.empNo || empIdKey);
            const vType = vigilanceTypeMap[empIdKey] ?? c.vigilanceType ?? c.vigilance_type ?? "No Vigilance Case";
            const cCase = criminalCaseMap[empIdKey] ?? (c.criminalCase ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : "No") : (c.criminal_case ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : "No") : "No"));
            const vRemarks = (vigilanceRemarksMap[empIdKey] ?? c.vigilanceRemarks ?? c.vigilance_remarks ?? (vType === "No Vigilance Case" ? "No vigilance or criminal case pending" : "")).trim();

            return {
              empId: empIdVal,
              vigilanceType: vType,
              criminalCase: cCase === "Yes" ? "yes" : "no",
              vigilanceRemarks: vRemarks
            };
          });

          const payload = {
            cycleId: Number(cycleId),
            candidates: payloadCandidates
          };

          await Api.post(API_URLS.PROMOTION.VIGILANCE_CLEARANCE, payload, true);
          toast.success("Vigilance Clearance details submitted successfully!");
          setStep(6);
        });
      } catch (error: any) {
        console.error("Vigilance Clearance submission failed:", error);
        toast.error("Vigilance Clearance submission failed: " + Api.getErrorMessage(error));
      }
    } else {
      setStep((s) => Math.min(6, s + 1));
    }
  };

  const downloadCandidatesCSV = async () => {
    if (!validateAparBenchmarks()) return;
    const rawCycleId = initiatedCycle?.cycleId || initiatedCycle?.id || cycleId || (queryCycleId ? Number(queryCycleId) : 1);
    const currentCycleId = typeof rawCycleId === "number" ? rawCycleId : (!isNaN(Number(rawCycleId)) ? Number(rawCycleId) : rawCycleId);

    if (!currentCycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }

    setIsDownloading(true);
    try {
      const payload = {
        cycleId: currentCycleId,
        aparRules: {
          general: {
            "4": Number(generalRules["4"]),
            "3": Number(generalRules["3"]),
            "2": Number(generalRules["2"])
          },
          scst: {
            "4": Number(scstRules["4"]),
            "3": Number(scstRules["3"]),
            "2": Number(scstRules["2"])
          }
        }
      };

      const response = await Api.downloadFile(
        API_URLS.PROMOTION.DOWNLOAD_ELIGIBLE_SERVICE_LIST,
        payload,
        true
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Eligible_Service_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Eligible Service List downloaded successfully!");
    } catch (error: any) {
      console.error("Failed to download Eligible Service List:", error);
      toast.error("Failed to download Eligible Service List: " + Api.getErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAPARUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!validateAparBenchmarks()) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = (event.target?.result as string).split(',')[1];
      try {
        await runWizardStep("Uploading and processing APAR Marks...", async () => {
          const currentCycleId = Number(cycleId || new URLSearchParams(location.search).get("cycleId") || 1);
          const payload = {
            cycleId: currentCycleId,
            aparRules: {
              general: {
                "4": Number(generalRules["4"]),
                "3": Number(generalRules["3"]),
                "2": Number(generalRules["2"])
              },
              scst: {
                "4": Number(scstRules["4"]),
                "3": Number(scstRules["3"]),
                "2": Number(scstRules["2"])
              }
            },
            file: base64,
            fileName: file.name
          };
          await Api.post(API_URLS.PROMOTION.APAR_SCORES, payload, true);
          toast.success("APAR Marks file uploaded and processed successfully!");
          setIsAparUploaded(true);
          setStep(4);
        });
      } catch (error: any) {
        console.error("APAR upload failed:", error);
        toast.error("APAR upload failed: " + Api.getErrorMessage(error));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      if (!cycleId) {
        toast.error("Cycle ID is missing. Please initiate the cycle again.");
        setIsGenerating(false);
        return;
      }
      await runWizardStep("Eligible list generation in progress...", async () => {
        await Api.post(API_URLS.PROMOTION.SUBMIT_FOR_DPC, { cycleId: Number(cycleId) }, true);
        let employeesList: any[] = eligibleDpcCandidates || [];
        let eligibleCount = 0;
        let listA = 0;
        let listB = 0;
        let listC = 0;
        let listD = 0;

        if (Array.isArray(employeesList)) {
          employeesList.forEach((e: any) => {
            const cls = (e.listClassification || e.listClass || "").toUpperCase().replace(/[\s_-]+/g, "");
            if (cls === "ELIGIBLEDPC" || cls === "ELIGIBLE") {
              eligibleCount++;
            } else if (cls === "LISTA") {
              listA++;
            } else if (cls === "LISTB") {
              listB++;
            } else if (cls === "LISTC") {
              listC++;
            } else if (cls === "LISTD" || cls === "LISTDE" || cls === "LISTE") {
              listD++;
            } else {
              eligibleCount++; // fallback
            }
          });
        }

        // Update stored cycle with counts
        try {
          const storedCycles = JSON.parse(localStorage.getItem("promotion_cycles") || "[]");
          const idx = storedCycles.findIndex((c: any) => c.id === cycleId || String(c.id) === String(cycleId));
          if (idx !== -1) {
            storedCycles[idx] = {
              ...storedCycles[idx],
              eligible: eligibleCount,
              lists: { A: listA, B: listB, C: listC, D: listD },
              status: "Active"
            };
            localStorage.setItem("promotion_cycles", JSON.stringify(storedCycles));
          }
        } catch (e) {
          console.error("Failed to update cycle counts in localStorage", e);
        }
      });

      toast.success("Eligible list generated successfully!");
      navigate(`${all_routes.PostPromotion.list}?cycleId=${cycleId}`, { state: { cycleId } });
    } catch (error: any) {
      toast.error("Failed to generate eligible list: " + Api.getErrorMessage(error));
    } finally {
      setIsGenerating(false);
    }
  };

  const filteredCandidatesStep2 = candidates.filter((c: any) => {
    if (!candidateMatchesSearch(c, searchQueryStep2)) return false;
    if (statusFilterStep2 === "PASSED") return servicePassed(c);
    if (statusFilterStep2 === "SHORTFALL") return serviceShortfall(c);
    return true;
  });

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Create New Promotion Cycle</h3>
            <p className="text-muted small mb-0">Follow the wizard to set up a new promotion cycle</p>
          </div>
          <div>
            <Link to={all_routes.PostPromotion.list} className="btn btn-light">Back to Lists</Link>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="mb-5 position-relative">
              <div
                className="position-absolute start-0 end-0 translate-middle-y"
                style={{ height: "3px", backgroundColor: "#e9ecef", zIndex: 1, top: "18px" }}
              ></div>
              <div className="d-flex justify-content-between align-items-center position-relative" style={{ zIndex: 2 }}>
                <StepBubble index={1} current={step} label="Cycle Details" onClick={() => cycleId && setStep(1)} />
                <StepBubble index={2} current={step} label="Service Rules" onClick={() => cycleId && setStep(2)} />
                <StepBubble index={3} current={step} label="APAR Rules" onClick={() => cycleId && setStep(3)} />
                <StepBubble index={4} current={step} label="D&AR Clearance" onClick={() => cycleId && setStep(4)} />
                <StepBubble index={5} current={step} label="Vigilance Clearance" onClick={() => cycleId && setStep(5)} />
                <StepBubble index={6} current={step} label="Review" onClick={() => cycleId && setStep(6)} />
              </div>
            </div>

            {/* Step content */}
            {step === 1 && (
              <>
                <h5 className="mb-3">Step 1/6: Cycle Details</h5>
                <p className="text-muted">Enter basic information about the promotion cycle</p>

                <div className="row g-3 mt-3">
                  <div className="col-md-4">
                    <label className="form-label">Cadre <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={cadre}
                      onChange={e => setCadre(e.target.value)}
                      disabled={cadre === "Non-Executive"}
                    >
                      {CADRE_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Financial Year <span className="text-danger">*</span></label>
                    <select
                      className="form-select bg-light"
                      value={financialYear}
                      disabled
                      onChange={e => setFinancialYear(e.target.value)}
                    >
                      {FINANCIAL_YEAR_OPTIONS.map((fy) => (
                        <option key={fy.value} value={fy.value}>{fy.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Employee Pay Grade Scale <span className="text-danger">*</span></label>
                    <Select
                      isMulti
                      isSearchable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{ Option: CheckboxOption, MenuList: CustomMenuList }}
                      options={gradesMaster.map((g: any) => ({
                        value: g.empPayscale || g.gradeCode || g.gradeFullName,
                        label: g.empPayscale || `${g.gradeFullName} ${g.gradeCode ? `(${g.gradeCode})` : ""}`
                      }))}
                      value={selectedGrades}
                      onChange={(selected: any) => setSelectedGrades(selected || [])}
                      onMenuSubmit={() => {
                        if (document.activeElement instanceof HTMLElement) {
                          document.activeElement.blur();
                        }
                      }}
                      placeholder="Search & select pay grade scale(s)..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Employee ID Range Filter</label>
                    <select
                      className="form-select"
                      value={employeeRange}
                      onChange={(e) => setEmployeeRange(e.target.value)}
                    >
                      <option value="">All Employees</option>
                      <option value="below_5000">Below 5000 (IDs &lt; 5000)</option>
                      <option value="above_5000">Above 5000 (IDs &ge; 5000)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Promotion Due On <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={cycleStartDate} onChange={e => setCycleStartDate(e.target.value)} />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="mb-4">
                  <h5 className="mb-1 fw-bold text-dark">Step 2/6: Service Rules</h5>
                  <p className="text-muted small mb-0">Configure service eligibility requirements and view calculation criteria</p>
                </div>

                <div className="row g-3 mb-3">
                  {/* Left Column: Service Rules Configuration */}
                  <div className="col-lg-6">
                    <div className="card h-100 border shadow-xs" style={{ borderRadius: "8px" }}>
                      <div className="card-header bg-light border-bottom py-2 px-3">
                        <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-1.5" style={{ fontSize: "13px" }}>
                          <i className="ti ti-adjustments-horizontal text-danger" style={{ fontSize: "15px" }}></i>
                          Service Rules Configuration
                        </h6>
                      </div>
                      <div className="card-body p-3 d-flex flex-column justify-content-between gap-2">
                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <label className="form-label fw-semibold text-dark mb-0" style={{ fontSize: "12px" }}>
                              Required Service Years
                            </label>
                            <span className="text-muted" style={{ fontSize: "11px" }}>(Default: 5 years)</span>
                          </div>
                          <input
                            type="number"
                            min={0}
                            className="form-control form-control-sm bg-light"
                            value={requiredServiceYears as any}
                            onChange={e => setRequiredServiceYears(e.target.value === "" ? "" : Number(e.target.value))}
                            disabled
                            style={{ borderRadius: "6px", fontSize: "12px", height: "32px" }}
                          />
                        </div>

                        <div className="p-2 px-3 rounded-2 border bg-light">
                          <div className="form-check form-switch mb-1.5">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="excludeLwp"
                              checked={excludeLWP}
                              onChange={e => setExcludeLWP(e.target.checked)}
                              disabled
                              style={{ cursor: "not-allowed", transform: "scale(0.85)" }}
                            />
                            <label className="form-check-label fw-medium text-dark ms-1" htmlFor="excludeLwp" style={{ fontSize: "12px" }}>
                              Exclude Leave Without Pay (LWP) from service
                            </label>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="excludeSuspension"
                              checked={excludeSuspension}
                              onChange={e => setExcludeSuspension(e.target.checked)}
                              disabled
                              style={{ cursor: "not-allowed", transform: "scale(0.85)" }}
                            />
                            <label className="form-check-label fw-medium text-dark ms-1" htmlFor="excludeSuspension" style={{ fontSize: "12px" }}>
                              Exclude Suspension Days from service
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Service Calculation Logic */}
                  <div className="col-lg-6">
                    <div className="card h-100 border shadow-xs" style={{ borderRadius: "8px", background: "linear-gradient(180deg, #f0f7ff 0%, #e6f0fa 100%)" }}>
                      <div className="card-header bg-transparent border-bottom py-2 px-3">
                        <h6 className="mb-0 fw-bold text-primary d-flex align-items-center gap-1.5" style={{ fontSize: "13px" }}>
                          <i className="ti ti-info-circle" style={{ fontSize: "15px" }}></i>
                          Service Calculation Logic
                        </h6>
                      </div>
                      <div className="card-body p-3">
                        <ul className="mb-0 d-flex flex-column gap-1.5 ps-3 text-secondary" style={{ fontSize: "12px", lineHeight: "1.45" }}>
                          <li>
                            <strong className="text-dark">Period:</strong> Date of Joining to Promotion Due On.
                          </li>
                          <li>
                            <strong className="text-dark">LWP Deductions:</strong> Leave Without Pay (LWP) days excluded.
                          </li>
                          <li>
                            <strong className="text-dark">Suspension Deductions:</strong> Suspension days excluded from total service.
                          </li>
                          <li>
                            <strong className="text-dark">Minimum Requirement:</strong> Minimum <span className="badge bg-primary px-1.5 py-0.5" style={{ fontSize: "10px" }}>{requiredServiceYears || 5} Years</span> required.
                          </li>
                          <li>
                            <strong className="text-dark">Exception:</strong> Shortfall candidates placed in <span className="badge bg-danger px-1.5 py-0.5" style={{ fontSize: "10px" }}>List A</span>.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: 8 }}>
                  <div className="card-header bg-transparent border-0 d-flex flex-column gap-3 py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-semibold text-dark">Candidates in This Cycle</h6>
                      <span className="badge bg-primary rounded-pill">{filteredCandidatesStep2.length} Candidates</span>
                    </div>
                    <div className="d-flex flex-column flex-md-row gap-2">
                      <div className="position-relative" style={{ maxWidth: 300, width: "100%" }}>
                        <input className="form-control form-control-sm ps-4" placeholder="Search candidate by name or ID..."
                          value={searchQueryStep2} onChange={(e) => { setSearchQueryStep2(e.target.value); setCurrentPage(1); }} />
                        <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                      </div>
                      <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={statusFilterStep2}
                        onChange={(e) => { setStatusFilterStep2(e.target.value); setCurrentPage(1); }}>
                        <option value="ALL">All</option><option value="PASSED">Passed</option><option value="SHORTFALL">Shortfall</option>
                      </select>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {loadingCandidates ? (
                      <div className="d-flex justify-content-center align-items-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : filteredCandidatesStep2.length === 0 ? (
                      <div className="text-center p-5 text-muted">
                        <i className="ti ti-users fs-1 mb-2 text-secondary"></i>
                        <p className="mb-0">No candidates match the selected search or status.</p>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th className="px-4 py-3 text-secondary">Emp ID</th>
                                <th className="py-3 text-secondary">Name</th>
                                <th className="py-3 text-secondary">Category</th>
                                <th className="py-3 text-secondary text-nowrap">Date of Joining</th>
                                <th className="py-3 text-secondary text-nowrap">Last Promotion Date</th>
                                <th className="py-3 text-secondary text-nowrap">Promotion Effective Date</th>
                                <th className="py-3 text-secondary">Current Grade & Designation</th>
                                <th className="py-3 text-secondary">Proposed Grade & Designation</th>
                                <th className="px-4 py-3 text-secondary text-end">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredCandidatesStep2
                                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                .map((c: any, idx: number) => {
                                  const dates = getCandidateDates(c);
                                  return (
                                    <tr key={c.emp_no || c.empId || idx}>
                                      <td className="px-4 fw-medium text-dark">{c.emp_no || c.empId}</td>
                                      <td><span className="fw-medium text-dark">{c.emp_name || c.empName}</span></td>
                                      <td>{getCategory(c)}</td>
                                      <td className="text-nowrap">{dates.joiningDate}</td>
                                      <td className="text-nowrap">
                                             <div>{dates.lastPromoDate}</div>
                                             <div className="small text-muted">LWP: {c.lwp_days ?? c.lwpDays ?? c.lwp ?? 0} Days</div>
                                              <div className="small text-muted">Deemed Suspension: {c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? 0} Days</div>
                                           </td>
                                      <td className="text-nowrap">{dates.effectivePromotionDate}</td>
                                      <td>
                                        <div>{c.old_grade || c.oldGrade}</div>
                                        <div className="small text-muted">{c.old_designation || c.oldDesignation}</div>
                                      </td>
                                      <td>
                                        <div>{c.new_grade || c.newGrade}</div>
                                        <div className="small text-muted">{c.new_designation || c.newDesignation}</div>
                                      </td>
                                      <td className="px-4 text-end">
                                        <span className={`badge ${c.promotion_status?.toLowerCase() === 'initiated' ? 'bg-info text-white' : 'bg-success text-white'}`}>
                                          {c.promotion_status || 'Initiated'}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination footer */}
                        <div className="card-footer bg-transparent border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 py-3 border-top">
                          <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted">Show</span>
                            <select
                              className="form-select form-select-sm"
                              style={{ width: 80 }}
                              value={pageSize}
                              onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span className="small text-muted">
                              Showing {filteredCandidatesStep2.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(filteredCandidatesStep2.length, currentPage * pageSize)} of {filteredCandidatesStep2.length} candidates
                            </span>
                          </div>
                          
                          {Math.ceil(filteredCandidatesStep2.length / pageSize) > 1 && (
                            <nav>
                              <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} style={{ cursor: 'pointer' }}>
                                    Previous
                                  </button>
                                </li>
                                {(() => {
                                  const totalPages = Math.ceil(filteredCandidatesStep2.length / pageSize);
                                  const maxVisiblePages = 5;
                                  let startPage = Math.max(1, currentPage - 2);
                                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                  if (endPage - startPage < maxVisiblePages - 1) {
                                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                  }
                                  const pages = [];
                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(i);
                                  }
                                  return pages.map((pageNo) => (
                                    <li key={pageNo} className={`page-item ${currentPage === pageNo ? 'active' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPage(pageNo)} style={{ cursor: 'pointer' }}>
                                        {pageNo}
                                      </button>
                                    </li>
                                  ));
                                })()}
                                <li className={`page-item ${currentPage === Math.ceil(filteredCandidatesStep2.length / pageSize) ? 'disabled' : ''}`}>
                                  <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredCandidatesStep2.length / pageSize), prev + 1))} style={{ cursor: 'pointer' }}>
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="mb-3">
                  <h5 className="mb-1 fw-bold text-dark">Step 3/6: APAR Rules</h5>
                  <p className="text-muted small mb-0">Set APAR qualification marks and view financial year evaluation logic</p>
                </div>

                <div className="row g-3 mb-3">
                  {/* Left Column: APAR Qualification Marks */}
                  <div className="col-lg-6">
                    <div className="card h-100 border shadow-xs" style={{ borderRadius: "8px" }}>
                      <div className="card-header bg-light border-bottom py-2 px-3">
                        <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-1.5" style={{ fontSize: "13px" }}>
                          <i className="ti ti-award text-warning" style={{ fontSize: "15px" }}></i>
                          APAR Qualification Marks
                        </h6>
                      </div>
                      <div className="card-body p-3 d-flex flex-column gap-3">
                        <div className="d-flex align-items-start justify-content-between gap-3 rounded-3 p-2" style={{ backgroundColor: "#fffbeb", border: "1px solid #fef3c7" }}>
                          <div className="small text-muted" style={{ fontSize: "11px", lineHeight: 1.5 }}>
                            Set minimum qualifying marks for different categories, evaluated across four APAR years.
                          </div>
                          <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle flex-shrink-0 px-2 py-1" style={{ fontSize: "10px" }}>
                            Total: 40 marks
                          </span>
                        </div>

                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-semibold text-dark" style={{ fontSize: "11px" }}>Qualifying benchmarks</span>
                            <span className="text-muted" style={{ fontSize: "9px" }}>Passing marks for 4, 3 & 2 APAR years</span>
                          </div>
                          
                          <div className="table-responsive rounded-3 border">
                            <table className="table table-sm align-middle mb-0 text-center" style={{ fontSize: "12px" }}>
                              <thead style={{ backgroundColor: "#f8fafc" }}>
                                <tr>
                                  <th className="text-start ps-3 py-2 text-secondary fw-semibold border-bottom" style={{ width: "34%", fontSize: "11px" }}>
                                    APAR Period
                                  </th>
                                  <th className="py-2 text-center border-bottom" style={{ width: "33%" }}>
                                    <div className="d-flex align-items-center justify-content-center gap-1.5">
                                      <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>General Category</span>
                                      <span className="badge bg-primary-subtle text-primary" style={{ fontSize: "9px" }}>General</span>
                                    </div>
                                  </th>
                                  <th className="py-2 text-center border-bottom" style={{ width: "33%" }}>
                                    <div className="d-flex align-items-center justify-content-center gap-1.5">
                                      <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>SC / ST Category</span>
                                      <span className="badge bg-success-subtle text-success" style={{ fontSize: "9px" }}>SC / ST</span>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="text-start ps-3 py-2 border-bottom">
                                    <div className="fw-bold text-dark" style={{ fontSize: "11px" }}>4 APAR Years</div>
                                    <span className="text-muted" style={{ fontSize: "9px" }}>Max: 40 marks</span>
                                  </td>
                                  <td className="px-2 py-2 border-bottom">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-bold text-dark"
                                        value={generalRules["4"]}
                                        onChange={e => {
                                          const val = Number(e.target.value);
                                          setGeneralRules({
                                            "4": val,
                                            "3": +(val * 0.75).toFixed(3),
                                            "2": +(val * 0.5).toFixed(3)
                                          });
                                        }}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 40</span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 border-bottom">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-bold text-dark"
                                        value={scstRules["4"]}
                                        onChange={e => {
                                          const val = Number(e.target.value);
                                          setScstRules({
                                            "4": val,
                                            "3": +(val * 0.75).toFixed(3),
                                            "2": +(val * 0.5).toFixed(3)
                                          });
                                        }}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 40</span>
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start ps-3 py-2 border-bottom">
                                    <div className="fw-bold text-dark" style={{ fontSize: "11px" }}>3 APAR Years</div>
                                    <span className="text-muted" style={{ fontSize: "9px" }}>Max: 30 marks</span>
                                  </td>
                                  <td className="px-2 py-2 border-bottom">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-semibold text-dark"
                                        value={generalRules["3"]}
                                        onChange={e => setGeneralRules(prev => ({ ...prev, "3": Number(e.target.value) }))}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 30</span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 border-bottom">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-semibold text-dark"
                                        value={scstRules["3"]}
                                        onChange={e => setScstRules(prev => ({ ...prev, "3": Number(e.target.value) }))}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 30</span>
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start ps-3 py-2">
                                    <div className="fw-bold text-dark" style={{ fontSize: "11px" }}>2 APAR Years</div>
                                    <span className="text-muted" style={{ fontSize: "9px" }}>Max: 20 marks</span>
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-semibold text-dark"
                                        value={generalRules["2"]}
                                        onChange={e => setGeneralRules(prev => ({ ...prev, "2": Number(e.target.value) }))}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 20</span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "135px" }}>
                                      <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        className="form-control bg-white text-center fw-semibold text-dark"
                                        value={scstRules["2"]}
                                        onChange={e => setScstRules(prev => ({ ...prev, "2": Number(e.target.value) }))}
                                        style={{ fontSize: "12px", height: "30px" }}
                                      />
                                      <span className="input-group-text bg-light text-muted px-1.5" style={{ fontSize: "10px" }}>/ 20</span>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-auto rounded-3 px-2 py-1.5" style={{ backgroundColor: "#f1f5f9" }}>
                          <i className="ti ti-info-circle text-secondary" style={{ fontSize: "13px" }}></i>
                          <span className="text-secondary" style={{ fontSize: "10px" }}>
                            Benchmarks accept up to 3 decimal places (for example, 22.756). Scores use the financial years shown in the FY Selection Logic panel.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: FY Selection Logic */}
                  <div className="col-lg-6">
                    <div className="card h-100 border shadow-xs" style={{ borderRadius: "8px", background: "linear-gradient(180deg, #f0fdf4 0%, #e6f7ec 100%)" }}>
                      <div className="card-header bg-transparent border-bottom py-2 px-3">
                        <h6 className="mb-0 fw-bold text-success d-flex align-items-center gap-1.5" style={{ fontSize: "13px" }}>
                          <i className="ti ti-calendar-event" style={{ fontSize: "15px" }}></i>
                          FY Selection Logic
                        </h6>
                      </div>
                      <div className="card-body p-3 d-flex flex-column justify-content-between">
                        <div>
                          <div className="small text-secondary mb-2" style={{ fontSize: "11px" }}>
                            Based on Promotion Due On, APAR scores from the following 4 financial years are evaluated:
                          </div>
                          {(() => {
                            const dueDate = promotionDateTo || promotionDateFrom;
                            const dueMonth = dueDate
                              ? new Date(`${dueDate}T00:00:00`).getMonth() + 1
                              : 6;
                            const rules = [
                              {
                                title: "Due up to June",
                                description: "Last four completed financial years are displayed.",
                                years: getDynamicLastFourFYs(financialYear, "2000-06-30"),
                                isApplied: dueMonth <= 6,
                              },
                              {
                                title: "Due from July onward",
                                description: "Last four financial years, including the current financial year, are displayed.",
                                years: getDynamicLastFourFYs(financialYear, "2000-07-01"),
                                isApplied: dueMonth >= 7,
                              },
                            ];

                            return (
                              <div className="d-flex flex-column gap-2 mb-2">
                                {rules.map((rule, ruleIndex) => (
                                  <div
                                    key={rule.title}
                                    className="rounded-3 p-2"
                                    style={{
                                      backgroundColor: rule.isApplied ? "#ffffff" : "rgba(255,255,255,0.45)",
                                      border: rule.isApplied ? "1px solid #22c55e" : "1px solid #dbe7df",
                                      boxShadow: rule.isApplied ? "0 2px 6px rgba(34, 197, 94, 0.10)" : "none",
                                    }}
                                  >
                                    <div className="d-flex align-items-start gap-2 mb-1.5">
                                      <span
                                        className={`badge rounded-circle ${rule.isApplied ? "bg-success" : "bg-secondary"}`}
                                        style={{ width: "20px", height: "20px", lineHeight: "14px", fontSize: "10px" }}
                                      >
                                        {ruleIndex + 1}
                                      </span>
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                          <strong className="text-dark" style={{ fontSize: "11px" }}>{rule.title}</strong>
                                          {rule.isApplied && (
                                            <span className="badge bg-success-subtle text-success" style={{ fontSize: "9px" }}>Applied</span>
                                          )}
                                        </div>
                                        <div className="text-secondary" style={{ fontSize: "10px" }}>{rule.description}</div>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-1 ms-4">
                                      {rule.years.map((fyStr) => (
                                        <span
                                          key={`${rule.title}-${fyStr}`}
                                          className={`badge border px-2 py-1 ${rule.isApplied ? "bg-success-subtle text-success border-success-subtle" : "bg-white text-secondary"}`}
                                          style={{ fontSize: "10px" }}
                                        >
                                          {fyStr}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="small text-muted" style={{ fontSize: "11px" }}>
                          <strong className="text-dark">Exception:</strong> Missing latest FY marks are placed in List A; other APAR failures are placed in List B.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const filteredPassedCandidates = passedServiceCandidates.filter((c: any) => {
                    if (!candidateMatchesSearch(c, searchQueryStep3)) return false;
                    if (statusFilterStep3 === "PASSED") return servicePassed(c);
                    if (statusFilterStep3 === "SHORTFALL") return serviceShortfall(c);
                    return true;
                  });
                  return (
                    <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: 8 }}>
                      <div className="card-header bg-transparent border-0 d-flex flex-column gap-3 py-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 fw-semibold text-dark">Candidates by Service Result</h6>
                          <span className="badge bg-success rounded-pill">{filteredPassedCandidates.length} Candidates</span>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between mt-1">
                          <div className="d-flex flex-column flex-md-row gap-2" style={{ width: '100%' }}>
                          <div className="position-relative" style={{ maxWidth: 300, width: '100%' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm ps-4"
                              placeholder="Search candidate by name or ID..."
                              value={searchQueryStep3}
                              onChange={(e) => { setSearchQueryStep3(e.target.value); setCurrentPageStep3(1); }}
                              style={{ height: 36, borderRadius: 6 }}
                            />
                            <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ fontSize: 14 }}></i>
                          </div>
                          <select className="form-select form-select-sm" style={{ maxWidth: 150, height: 36 }} value={statusFilterStep3}
                            onChange={(e) => { setStatusFilterStep3(e.target.value); setCurrentPageStep3(1); }}>
                            <option value="ALL">All Service Results</option><option value="PASSED">Service Passed</option><option value="SHORTFALL">Service Shortfall</option>
                          </select>
                          </div>
                          
                          <div className="d-flex align-items-center gap-2">
                            <button
                              className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1.5"
                              style={{ height: 36, whiteSpace: 'nowrap', borderRadius: 6 }}
                              onClick={downloadCandidatesCSV}
                              disabled={isDownloading}
                            >
                              {isDownloading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="ti ti-download fs-6"></i> Download List
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {loadingPassedCandidates ? (
                          <div className="d-flex justify-content-center align-items-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPassedCandidates.length === 0 ? (
                          <div className="text-center p-5 text-muted">
                            <i className="ti ti-users fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">No candidates match the selected search or status.</p>
                          </div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th className="px-4 py-3 text-secondary">Emp ID</th>
                                    <th className="py-3 text-secondary">Name</th>
                                    <th className="py-3 text-secondary">Category</th>
                                    <th className="py-3 text-secondary text-nowrap">Date of Joining</th>
                                    <th className="py-3 text-secondary text-nowrap">Last Promotion Date</th>
                                    <th className="py-3 text-secondary text-nowrap">Promotion Effective Date</th>
                                    <th className="py-3 text-secondary">Service (Years)</th>
                                    <th className="py-3 text-secondary">Current Grade & Designation</th>
                                    <th className="py-3 text-secondary">Proposed Grade & Designation</th>
                                    <th className="px-4 py-3 text-secondary text-end">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPassedCandidates
                                    .slice((currentPageStep3 - 1) * pageSizeStep3, currentPageStep3 * pageSizeStep3)
                                    .map((c: any, idx: number) => {
                                      const dates = getCandidateDates(c);
                                      return (
                                        <tr key={c.emp_no || c.empId || idx}>
                                          <td className="px-4 fw-medium text-dark">{c.emp_no || c.empId}</td>
                                          <td><span className="fw-medium text-dark">{c.emp_name || c.empName}</span></td>
                                          <td>{getCategory(c)}</td>
                                          <td className="text-nowrap">{dates.joiningDate}</td>
                                          <td className="text-nowrap">
                                             <div>{dates.lastPromoDate}</div>
                                             <div className="small text-muted">LWP: {c.lwp_days ?? c.lwpDays ?? c.lwp ?? 0} Days</div>
                                              <div className="small text-muted">Deemed Suspension: {c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? 0} Days</div>
                                           </td>
                                          <td className="text-nowrap">{dates.effectivePromotionDate}</td>
                                          <td>{c.service_years || c.serviceYears} years</td>
                                          <td>
                                            <div>{c.old_grade || c.oldGrade}</div>
                                            <div className="small text-muted">{c.old_designation || c.oldDesignation}</div>
                                          </td>
                                          <td>
                                            <div>{c.new_grade || c.newGrade}</div>
                                            <div className="small text-muted">{c.new_designation || c.newDesignation}</div>
                                          </td>
                                          <td className="px-4 text-end">
                                            <span className={`badge ${servicePassed(c) ? 'bg-success text-white' : serviceShortfall(c) ? 'bg-danger text-white' : 'bg-secondary text-white'}`}>
                                              {servicePassed(c) ? 'Passed' : serviceShortfall(c) ? 'Shortfall' : 'Pending'}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="card-footer bg-transparent border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 py-3 border-top">
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">Show</span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 80 }}
                                  value={pageSizeStep3}
                                  onChange={(e) => {
                                    setPageSizeStep3(Number(e.target.value));
                                    setCurrentPageStep3(1);
                                  }}
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                </select>
                                <span className="small text-muted">
                                  Showing {filteredPassedCandidates.length === 0 ? 0 : (currentPageStep3 - 1) * pageSizeStep3 + 1} to {Math.min(filteredPassedCandidates.length, currentPageStep3 * pageSizeStep3)} of {filteredPassedCandidates.length} candidates
                                </span>
                              </div>
                              
                              {Math.ceil(filteredPassedCandidates.length / pageSizeStep3) > 1 && (
                                <nav>
                                  <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPageStep3 === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep3(prev => Math.max(1, prev - 1))} style={{ cursor: 'pointer' }}>
                                        Previous
                                      </button>
                                    </li>
                                    {(() => {
                                      const totalPages = Math.ceil(filteredPassedCandidates.length / pageSizeStep3);
                                      const maxVisiblePages = 5;
                                      let startPage = Math.max(1, currentPageStep3 - 2);
                                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                      if (endPage - startPage < maxVisiblePages - 1) {
                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                      }
                                      const pages = [];
                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(i);
                                      }
                                      return pages.map((pageNo) => (
                                        <li key={pageNo} className={`page-item ${currentPageStep3 === pageNo ? 'active' : ''}`}>
                                          <button className="page-link" onClick={() => setCurrentPageStep3(pageNo)} style={{ cursor: 'pointer' }}>
                                            {pageNo}
                                          </button>
                                        </li>
                                      ));
                                    })()}
                                    <li className={`page-item ${currentPageStep3 === Math.ceil(filteredPassedCandidates.length / pageSizeStep3) ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep3(prev => Math.min(Math.ceil(filteredPassedCandidates.length / pageSizeStep3), prev + 1))} style={{ cursor: 'pointer' }}>
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {step === 4 && (
              <>
                <h5 className="mb-3">Step 4/6: D&AR Rules</h5>
                <p className="text-muted">Configure D&AR clearance check rules</p>

                <div className="mb-3 p-3" style={{ background: '#fff7e6', borderRadius: 6 }}>
                  <strong>D&AR Clearance Policy</strong>
                  <div className="small text-muted">Checks all candidates against pending and contemplated D&AR inquiry records. Candidates with active cases will be classified into List C.</div>
                </div>

                {(() => {
                  const filteredPassedApar = passedAparCandidates.filter((c: any) => {
                    if (!candidateMatchesSearch(c, searchQueryStep4)) return false;
                    if (statusFilterStep4 === "PASSED") return aparPassed(c);
                    if (statusFilterStep4 === "FAILED") return aparFailed(c);
                    return true;
                  });
                  return (
                    <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: 8 }}>
                      <div className="card-header bg-transparent border-0 d-flex flex-column gap-3 py-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 fw-semibold text-dark">Candidates for D&AR Review</h6>
                          <span className="badge bg-success rounded-pill">{filteredPassedApar.length} Candidates</span>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between mt-1">
                          <div className="d-flex flex-column flex-md-row gap-2" style={{ width: '100%' }}>
                          <div className="position-relative" style={{ maxWidth: 300, width: '100%' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm ps-4"
                              placeholder="Search candidate by name or ID..."
                              value={searchQueryStep4}
                              onChange={(e) => { setSearchQueryStep4(e.target.value); setCurrentPageStep4(1); }}
                              style={{ height: 36, borderRadius: 6 }}
                            />
                            <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ fontSize: 14 }}></i>
                          </div>
                          <select className="form-select form-select-sm" style={{ maxWidth: 160, height: 36 }} value={statusFilterStep4}
                            onChange={(e) => { setStatusFilterStep4(e.target.value); setCurrentPageStep4(1); }}>
                            <option value="ALL">All APAR Results</option><option value="PASSED">APAR Passed</option><option value="FAILED">APAR Failed</option>
                          </select>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {loadingPassedAparCandidates ? (
                          <div className="d-flex justify-content-center align-items-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPassedApar.length === 0 ? (
                          <div className="text-center p-5 text-muted">
                            <i className="ti ti-users fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">No candidates match the selected search or status.</p>
                          </div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th className="px-4 py-3 text-secondary">Emp ID</th>
                                    <th className="py-3 text-secondary">Name</th>
                                    <th className="py-3 text-secondary">Category</th>
                                    <th className="py-3 text-secondary text-nowrap">Date of Joining</th>
                                    <th className="py-3 text-secondary text-nowrap">Last Promotion Date</th>
                                    <th className="py-3 text-secondary text-nowrap">Promotion Effective Date</th>
                                    <th className="py-3 text-secondary">Service (Years)</th>
                                    <th className="py-3 text-secondary">Total APAR Score</th>
                                    <th className="py-3 text-secondary text-nowrap">APAR Remarks</th>
                                    <th className="px-4 py-3 text-secondary text-end">APAR Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPassedApar
                                    .slice((currentPageStep4 - 1) * pageSizeStep4, currentPageStep4 * pageSizeStep4)
                                    .map((c: any, idx: number) => {
                                      const dates = getCandidateDates(c);
                                      return (
                                        <tr key={c.emp_no || c.empId || idx}>
                                          <td className="px-4 fw-medium text-dark">{c.emp_no || c.empId}</td>
                                          <td><span className="fw-medium text-dark">{c.emp_name || c.empName}</span></td>
                                          <td>{getCategory(c)}</td>
                                          <td className="text-nowrap">{dates.joiningDate}</td>
                                          <td className="text-nowrap">
                                             <div>{dates.lastPromoDate}</div>
                                             <div className="small text-muted">LWP: {c.lwp_days ?? c.lwpDays ?? c.lwp ?? 0} Days</div>
                                              <div className="small text-muted">Deemed Suspension: {c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? 0} Days</div>
                                           </td>
                                          <td className="text-nowrap">{dates.effectivePromotionDate}</td>
                                          <td>{c.service_years || c.serviceYears} years</td>
                                          <td>
                                            <div className="fw-semibold text-danger">{formatAparScore(c.apar_total_score || c.aparTotalScore)}</div>
                                          </td>
                                          <td style={{ minWidth: "160px", maxWidth: "260px" }}>
                                            <div className="text-secondary small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                              {getCandidateAparRemarks(c)}
                                            </div>
                                          </td>
                                          <td className="px-4 text-end">
                                            {c.checks?.apar === 'PENDING' ? (<span className="badge bg-secondary text-white">Pending</span>) : c.apar_suitable === false || c.apar_evaluation?.isSuitable === false || c.listClassification === 'LIST_B' || c.apar_status === 'FAIL' || c.apar_status === 'NOT_SUITABLE' || c.is_suitable === false ? (
                                              <span className="badge bg-danger text-white">Not Suitable</span>
                                            ) : (
                                              <span className="badge bg-success text-white">Suitable</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="card-footer bg-transparent border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 py-3 border-top">
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">Show</span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 80 }}
                                  value={pageSizeStep4}
                                  onChange={(e) => {
                                    setPageSizeStep4(Number(e.target.value));
                                    setCurrentPageStep4(1);
                                  }}
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                </select>
                                <span className="small text-muted">
                                  Showing {filteredPassedApar.length === 0 ? 0 : (currentPageStep4 - 1) * pageSizeStep4 + 1} to {Math.min(filteredPassedApar.length, currentPageStep4 * pageSizeStep4)} of {filteredPassedApar.length} candidates
                                </span>
                              </div>
                              
                              {Math.ceil(filteredPassedApar.length / pageSizeStep4) > 1 && (
                                <nav>
                                  <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPageStep4 === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep4(prev => Math.max(1, prev - 1))} style={{ cursor: 'pointer' }}>
                                        Previous
                                      </button>
                                    </li>
                                    {(() => {
                                      const totalPages = Math.ceil(filteredPassedApar.length / pageSizeStep4);
                                      const maxVisiblePages = 5;
                                      let startPage = Math.max(1, currentPageStep4 - 2);
                                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                      if (endPage - startPage < maxVisiblePages - 1) {
                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                      }
                                      const pages = [];
                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(i);
                                      }
                                      return pages.map((pageNo) => (
                                        <li key={pageNo} className={`page-item ${currentPageStep4 === pageNo ? 'active' : ''}`}>
                                          <button className="page-link" onClick={() => setCurrentPageStep4(pageNo)} style={{ cursor: 'pointer' }}>
                                            {pageNo}
                                          </button>
                                        </li>
                                      ));
                                    })()}
                                    <li className={`page-item ${currentPageStep4 === Math.ceil(filteredPassedApar.length / pageSizeStep4) ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep4(prev => Math.min(Math.ceil(filteredPassedApar.length / pageSizeStep4), prev + 1))} style={{ cursor: 'pointer' }}>
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {step === 5 && (
              <>
                <h5 className="mb-3">Step 5/6: Vigilance Rules</h5>
                <p className="text-muted">Configure Vigilance clearance check rules</p>

                <div className="mb-3 p-3" style={{ background: '#e8f4fd', borderRadius: 6 }}>
                  <strong>Vigilance Clearance Policy</strong>
                  <div className="small text-muted">Checks all candidates against active Vigilance inquiry cases. Candidates with active vigilance or criminal cases will be classified into List D.</div>
                </div>

                {(() => {
                  const filteredPassedDar = passedDarCandidates.filter((c: any) => {
                    if (!candidateMatchesSearch(c, searchQueryStep5)) return false;
                    if (statusFilterStep5 === "CLEAR") return darClear(c);
                    if (statusFilterStep5 === "NOT_CLEAR") return darNotClear(c);
                    return true;
                  });
                  return (
                    <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: 8 }}>
                      <div className="card-header bg-transparent border-0 d-flex flex-column gap-3 py-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 fw-semibold text-dark">Candidates by D&amp;AR Result</h6>
                          <span className="badge bg-success rounded-pill">{filteredPassedDar.length} Candidates</span>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between mt-1">
                          <div className="d-flex flex-column flex-md-row gap-2" style={{ width: '100%' }}>
                          <div className="position-relative" style={{ maxWidth: 300, width: '100%' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm ps-4"
                              placeholder="Search candidate by name or ID..."
                              value={searchQueryStep5}
                              onChange={(e) => { setSearchQueryStep5(e.target.value); setCurrentPageStep5(1); }}
                              style={{ height: 36, borderRadius: 6 }}
                            />
                            <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ fontSize: 14 }}></i>
                          </div>
                          <select className="form-select form-select-sm" style={{ maxWidth: 170, height: 36 }} value={statusFilterStep5}
                            onChange={(e) => { setStatusFilterStep5(e.target.value); setCurrentPageStep5(1); }}>
                            <option value="ALL">All D&amp;AR Results</option><option value="CLEAR">D&amp;AR Clear</option><option value="NOT_CLEAR">D&amp;AR Not Clear</option>
                          </select>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {loadingPassedDarCandidates ? (
                          <div className="d-flex justify-content-center align-items-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPassedDar.length === 0 ? (
                          <div className="text-center p-5 text-muted">
                            <i className="ti ti-users fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">No candidates match the selected search or status.</p>
                          </div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th className="px-4 py-3 text-secondary">Emp ID</th>
                                    <th className="py-3 text-secondary">Name</th>
                                    <th className="py-3 text-secondary">Category</th>
                                    <th className="py-3 text-secondary text-nowrap">Date of Joining</th>
                                    <th className="py-3 text-secondary text-nowrap">Last Promotion Date</th>
                                    <th className="py-3 text-secondary text-nowrap">Promotion Effective Date</th>
                                    <th className="py-3 text-secondary text-nowrap">Service (Years)</th>
                                    <th className="py-3 text-secondary text-nowrap">Total APAR Score</th>
                                    <th className="py-3 text-secondary text-nowrap">APAR Remarks</th>
                                    <th className="py-3 text-secondary text-nowrap">D&AR Status</th>
                                    <th className="py-3 text-secondary text-nowrap text-center">D&AR Details</th>
                                    <th className="py-3 text-secondary text-nowrap">Vigilance Type</th>
                                    <th className="py-3 text-secondary text-nowrap">Criminal Case</th>
                                    <th className="py-3 text-secondary text-nowrap" style={{ minWidth: "220px" }}>Vigilance Remarks</th>
                                    <th className="px-4 py-3 text-secondary text-end text-nowrap">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPassedDar
                                    .slice((currentPageStep5 - 1) * pageSizeStep5, currentPageStep5 * pageSizeStep5)
                                    .map((c: any, idx: number) => {
                                      const dates = getCandidateDates(c);
                                      const dar = getDarDetails(c);
                                      const empIdKey = String(c.emp_no || c.empId || c.empNo || idx);
                                      const currentVigType = vigilanceTypeMap[empIdKey] ?? c.vigilanceType ?? c.vigilance_type ?? "No Vigilance Case";
                                      const currentCrimCase = criminalCaseMap[empIdKey] ?? (c.criminalCase ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : "No") : (c.criminal_case ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : "No") : "No"));
                                      const currentRemark = vigilanceRemarksMap[empIdKey] ?? c.vigilanceRemarks ?? c.vigilance_remarks ?? (currentVigType === "No Vigilance Case" ? "No vigilance or criminal case pending" : "");
                                      const hasError = !!vigilanceErrorsMap[empIdKey];
                                      const isMandatory = currentVigType !== "No Vigilance Case";

                                      return (
                                        <tr key={empIdKey} className={hasError ? "table-danger" : ""}>
                                          <td className="px-4 fw-medium text-dark">{empIdKey}</td>
                                          <td><span className="fw-medium text-dark">{c.emp_name || c.empName}</span></td>
                                          <td>{getCategory(c)}</td>
                                          <td className="text-nowrap">{dates.joiningDate}</td>
                                          <td className="text-nowrap">
                                             <div>{dates.lastPromoDate}</div>
                                             <div className="small text-muted">LWP: {c.lwp_days ?? c.lwpDays ?? c.lwp ?? 0} Days</div>
                                              <div className="small text-muted">Deemed Suspension: {c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? 0} Days</div>
                                           </td>
                                          <td className="text-nowrap">{dates.effectivePromotionDate}</td>
                                          <td className="text-nowrap">{c.service_years || c.serviceYears} years</td>
                                          <td className="text-nowrap">
                                            <div className="fw-semibold text-danger">{formatAparScore(c.apar_total_score || c.aparTotalScore)}</div>
                                          </td>
                                          <td style={{ minWidth: "160px", maxWidth: "260px" }}>
                                            <div className="text-secondary small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                              {getCandidateAparRemarks(c)}
                                            </div>
                                          </td>
                                          <td className="text-nowrap">
                                            <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>{dar.statusText}</span>
                                          </td>
                                          <td className="text-nowrap text-center">
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
                                              style={{ fontSize: "11px", borderRadius: "5px" }}
                                              onClick={() => setSelectedDarCandidate(c)}
                                            >
                                              <i className="ti ti-eye fs-6"></i> View Details
                                            </button>
                                          </td>
                                          <td className="text-nowrap" style={{ minWidth: "170px" }}>
                                            <select
                                              className={`form-select form-select-sm fw-semibold ${currentVigType === "No Vigilance Case" ? "text-success border-success-subtle" : "text-warning-emphasis border-warning-subtle"}`}
                                              value={currentVigType}
                                              onChange={(e) => {
                                                const newType = e.target.value;
                                                setVigilanceTypeMap((prev) => ({ ...prev, [empIdKey]: newType }));
                                                setVigilanceErrorsMap((prev) => ({ ...prev, [empIdKey]: "" }));
                                                const existingRemark = vigilanceRemarksMap[empIdKey] ?? c.vigilanceRemarks ?? c.vigilance_remarks ?? "";
                                                if (newType === "No Vigilance Case") {
                                                  if (!existingRemark || existingRemark === "No vigilance or criminal case pending") {
                                                    setVigilanceRemarksMap((prev) => ({ ...prev, [empIdKey]: "No vigilance or criminal case pending" }));
                                                  }
                                                } else {
                                                  if (existingRemark === "No vigilance or criminal case pending") {
                                                    setVigilanceRemarksMap((prev) => ({ ...prev, [empIdKey]: "" }));
                                                  }
                                                }
                                              }}
                                            >
                                              <option value="No Vigilance Case">No Vigilance Case</option>
                                              <option value="Vigilance Initiated">Vigilance Initiated</option>
                                              <option value="Penalty Awarded">Penalty Awarded</option>
                                            </select>
                                          </td>
                                          <td className="text-nowrap" style={{ width: "110px" }}>
                                            <select
                                              className={`form-select form-select-sm fw-semibold ${currentCrimCase === "Yes" ? "text-danger border-danger-subtle" : "text-dark"}`}
                                              value={currentCrimCase}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                setCriminalCaseMap((prev) => ({ ...prev, [empIdKey]: val }));
                                              }}
                                            >
                                              <option value="No">No</option>
                                              <option value="Yes">Yes</option>
                                            </select>
                                          </td>
                                          <td style={{ minWidth: "220px" }}>
                                            <div className="position-relative">
                                              <input
                                                type="text"
                                                className={`form-control form-control-sm ${hasError ? "is-invalid border-danger" : ""}`}
                                                placeholder={isMandatory ? "Remarks required *" : "Enter remarks..."}
                                                value={currentRemark}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setVigilanceRemarksMap((prev) => ({ ...prev, [empIdKey]: val }));
                                                  if (val.trim()) {
                                                    setVigilanceErrorsMap((prev) => ({ ...prev, [empIdKey]: "" }));
                                                  }
                                                }}
                                                style={{ fontSize: "12px" }}
                                              />
                                              {hasError && (
                                                <div className="invalid-feedback d-block mt-0.5" style={{ fontSize: "10px" }}>
                                                  {vigilanceErrorsMap[empIdKey]}
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-4 text-end text-nowrap">
                                            <span className="badge bg-success text-white">D&AR Cleared</span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="card-footer bg-transparent border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 py-3 border-top">
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">Show</span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 80 }}
                                  value={pageSizeStep5}
                                  onChange={(e) => {
                                    setPageSizeStep5(Number(e.target.value));
                                    setCurrentPageStep5(1);
                                  }}
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                </select>
                                <span className="small text-muted">
                                  Showing {filteredPassedDar.length === 0 ? 0 : (currentPageStep5 - 1) * pageSizeStep5 + 1} to {Math.min(filteredPassedDar.length, currentPageStep5 * pageSizeStep5)} of {filteredPassedDar.length} candidates
                                </span>
                              </div>
                              
                              {Math.ceil(filteredPassedDar.length / pageSizeStep5) > 1 && (
                                <nav>
                                  <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPageStep5 === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep5(prev => Math.max(1, prev - 1))} style={{ cursor: 'pointer' }}>
                                        Previous
                                      </button>
                                    </li>
                                    {(() => {
                                      const totalPages = Math.ceil(filteredPassedDar.length / pageSizeStep5);
                                      const maxVisiblePages = 5;
                                      let startPage = Math.max(1, currentPageStep5 - 2);
                                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                      if (endPage - startPage < maxVisiblePages - 1) {
                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                      }
                                      const pages = [];
                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(i);
                                      }
                                      return pages.map((pageNo) => (
                                        <li key={pageNo} className={`page-item ${currentPageStep5 === pageNo ? 'active' : ''}`}>
                                          <button className="page-link" onClick={() => setCurrentPageStep5(pageNo)} style={{ cursor: 'pointer' }}>
                                            {pageNo}
                                          </button>
                                        </li>
                                      ));
                                    })()}
                                    <li className={`page-item ${currentPageStep5 === Math.ceil(filteredPassedDar.length / pageSizeStep5) ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep5(prev => Math.min(Math.ceil(filteredPassedDar.length / pageSizeStep5), prev + 1))} style={{ cursor: 'pointer' }}>
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {step === 6 && (
              <>
                <h5 className="mb-3">Step 6/6: Review & Generate</h5>
                <p className="text-muted">Review all details and generate final eligible list</p>

                {initiateMessage && (
                  <div className="alert alert-success border-0 shadow-sm d-flex align-items-center mb-3" style={{ borderRadius: 8, background: '#eafaf1', color: '#14532d' }}>
                    <i className="ti ti-circle-check text-success fs-5 me-2"></i>
                    <div>{initiateMessage}</div>
                  </div>
                )}

                <div className="mb-3 p-3" style={{ background: '#f3f6ff', borderRadius: 8 }}>
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-3" style={{ fontWeight: 600, color: '#1e3a8a' }}>Cycle Information</h6>
                      <div className="mb-2"><strong>Promotion Cycle ID:</strong> {initiatedCycle?.promotionCycleId || '-'} </div>
                      <div className="mb-2"><strong>Cycle Name:</strong> {initiatedCycle?.cycleName || cycleName || '-'} </div>
                      <div className="mb-2"><strong>Cadre:</strong> {initiatedCycle?.cadre || cadre}</div>
                      <div className="mb-2"><strong>Financial Year:</strong> {initiatedCycle?.financialYear || financialYear}</div>
                      <div className="mb-2"><strong>Employee Pay Grade Scale:</strong> {initiatedCycle?.grades ? (typeof initiatedCycle.grades === 'string' ? JSON.parse(initiatedCycle.grades).join(', ') : initiatedCycle.grades.join(', ')) : selectedGrades.map((g: any) => g.value).join(', ') || '-'}</div>
                      <div className="mb-2"><strong>Employee ID Range Filter:</strong> {employeeRange === 'below_5000' ? 'Below 5000 (IDs < 5000)' : employeeRange === 'above_5000' ? 'Above 5000 (IDs >= 5000)' : 'All Employees'}</div>
                      <div className="mb-2"><strong>Promotion Due On:</strong> {initiatedCycle?.cycleStartDate || initiatedCycle?.promotionDateFrom || cycleStartDate || '-'}</div>
                      <div className="mb-2"><strong>Status:</strong> <span className={`badge ${initiatedCycle?.status === "INITIATED" ? "bg-success" : "bg-info"}`}>{initiatedCycle?.status || 'INITIATED'}</span></div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="mb-3" style={{ fontWeight: 600, color: '#1e3a8a' }}>Eligibility Criteria</h6>
                      <div className="mb-2"><strong>Service Required:</strong> {requiredServiceYears || 5} years</div>
                      <div className="mb-2"><strong>LWP Exclusion:</strong> {excludeLWP ? 'Yes' : 'No'}</div>
                      <div className="mb-2"><strong>Suspension Exclusion:</strong> {excludeSuspension ? 'Yes' : 'No'}</div>
                      <div className="mb-2"><strong>General Min Marks:</strong> {generalMarks}/40</div>
                      <div className="mb-2"><strong>SC/ST Min Marks:</strong> {scstMarks}/40</div>
                    </div>
                  </div>
                </div>

                <div className="mb-3 p-3" style={{ background: '#fff7d6', borderRadius: 6 }}>
                  <strong>Important Note</strong>
                  <ul className="mb-0" style={{ paddingLeft: 18 }}>
                    <li>Evaluate and finalize list of fully cleared candidates</li>
                    <li>Ensure DPC review includes all fully qualified employees</li>
                  </ul>
                </div>

                {(() => {
                  const filteredEligible = eligibleDpcCandidates.filter((c: any) => {
                    if (!candidateMatchesSearch(c, searchQueryStep6)) return false;
                    if (statusFilterStep6 === "SUITABLE") return vigilanceSuitable(c);
                    if (statusFilterStep6 === "NOT_SUITABLE") return !vigilanceSuitable(c);
                    return true;
                  });
                  return (
                    <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: 8 }}>
                      <div className="card-header bg-transparent border-0 d-flex flex-column gap-3 py-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 fw-semibold text-dark">Candidates by Vigilance Result</h6>
                          <span className="badge bg-success rounded-pill">{filteredEligible.length} Candidates</span>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between mt-1">
                          <div className="d-flex flex-column flex-md-row gap-2" style={{ width: '100%' }}>
                          <div className="position-relative" style={{ maxWidth: 300, width: '100%' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm ps-4"
                              placeholder="Search candidate by name or ID..."
                              value={searchQueryStep6}
                              onChange={(e) => { setSearchQueryStep6(e.target.value); setCurrentPageStep6(1); }}
                              style={{ height: 36, borderRadius: 6 }}
                            />
                            <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ fontSize: 14 }}></i>
                          </div>
                          <select className="form-select form-select-sm" style={{ maxWidth: 160, height: 36 }} value={statusFilterStep6}
                            onChange={(e) => { setStatusFilterStep6(e.target.value); setCurrentPageStep6(1); }}>
                            <option value="ALL">All Vigilance Results</option><option value="SUITABLE">Vigilance Suitable</option><option value="NOT_SUITABLE">Vigilance Not Suitable</option>
                          </select>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {loadingEligibleDpc ? (
                          <div className="d-flex justify-content-center align-items-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredEligible.length === 0 ? (
                          <div className="text-center p-5 text-muted">
                            <i className="ti ti-users fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">No candidates match the selected search or status.</p>
                          </div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th className="px-4 py-3 text-secondary">Emp ID</th>
                                    <th className="py-3 text-secondary">Name</th>
                                    <th className="py-3 text-secondary">Category</th>
                                    <th className="py-3 text-secondary text-nowrap">Date of Joining</th>
                                    <th className="py-3 text-secondary text-nowrap">Last Promotion Date</th>
                                    <th className="py-3 text-secondary text-nowrap">Promotion Effective Date</th>
                                    <th className="py-3 text-secondary">Current Pay Scale & Grade</th>
                                    <th className="py-3 text-secondary">Service (Years)</th>
                                    <th className="py-3 text-secondary">APAR Score</th>
                                    <th className="py-3 text-secondary text-nowrap">APAR Remarks</th>
                                    <th className="px-4 py-3 text-secondary text-end">Clearances</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredEligible
                                    .slice((currentPageStep6 - 1) * pageSizeStep6, currentPageStep6 * pageSizeStep6)
                                    .map((c: any, idx: number) => {
                                      const dates = getCandidateDates(c);
                                      return (
                                        <tr key={c.empId || c.emp_no || idx}>
                                          <td className="px-4 fw-medium text-dark">{c.empId || c.emp_no}</td>
                                          <td><span className="fw-medium text-dark">{c.employeeName || c.empName || c.emp_name}</span></td>
                                          <td>{getCategory(c)}</td>
                                          <td className="text-nowrap">{dates.joiningDate}</td>
                                          <td className="text-nowrap">
                                             <div>{dates.lastPromoDate}</div>
                                             <div className="small text-muted">LWP: {c.lwp_days ?? c.lwpDays ?? c.lwp ?? 0} Days</div>
                                              <div className="small text-muted">Deemed Suspension: {c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? 0} Days</div>
                                           </td>
                                          <td className="text-nowrap">{dates.effectivePromotionDate}</td>
                                          <td>
                                            <div>{c.currentPayscale || c.current_payscale}</div>
                                            <div className="small text-muted">{c.old_grade || c.oldGrade}</div>
                                          </td>
                                          <td>{c.serviceYears || c.service_years} years</td>
                                          <td>
                                            <div className="fw-semibold text-danger">{formatAparScore(c.aparTotalScore || c.apar_total_score)}</div>
                                          </td>
                                          <td style={{ minWidth: "160px", maxWidth: "260px" }}>
                                            <div className="text-secondary small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                              {getCandidateAparRemarks(c)}
                                            </div>
                                          </td>
                                          <td className="px-4 text-end">
                                            <span className={`badge ${vigilanceSuitable(c) ? 'bg-success' : 'bg-danger'} text-white`}>
                                              {vigilanceSuitable(c) ? 'Suitable' : 'Not Suitable'}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="card-footer bg-transparent border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 py-3 border-top">
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">Show</span>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 80 }}
                                  value={pageSizeStep6}
                                  onChange={(e) => {
                                    setPageSizeStep6(Number(e.target.value));
                                    setCurrentPageStep6(1);
                                  }}
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                </select>
                                <span className="small text-muted">
                                  Showing {filteredEligible.length === 0 ? 0 : (currentPageStep6 - 1) * pageSizeStep6 + 1} to {Math.min(filteredEligible.length, currentPageStep6 * pageSizeStep6)} of {filteredEligible.length} candidates
                                </span>
                              </div>
                              
                              {Math.ceil(filteredEligible.length / pageSizeStep6) > 1 && (
                                <nav>
                                  <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPageStep6 === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep6(prev => Math.max(1, prev - 1))} style={{ cursor: 'pointer' }}>
                                        Previous
                                      </button>
                                    </li>
                                    {(() => {
                                      const totalPages = Math.ceil(filteredEligible.length / pageSizeStep6);
                                      const maxVisiblePages = 5;
                                      let startPage = Math.max(1, currentPageStep6 - 2);
                                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                      if (endPage - startPage < maxVisiblePages - 1) {
                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                      }
                                      const pages = [];
                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(i);
                                      }
                                      return pages.map((pageNo) => (
                                        <li key={pageNo} className={`page-item ${currentPageStep6 === pageNo ? 'active' : ''}`}>
                                          <button className="page-link" onClick={() => setCurrentPageStep6(pageNo)} style={{ cursor: 'pointer' }}>
                                            {pageNo}
                                          </button>
                                        </li>
                                      ));
                                    })()}
                                    <li className={`page-item ${currentPageStep6 === Math.ceil(filteredEligible.length / pageSizeStep6) ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => setCurrentPageStep6(prev => Math.min(Math.ceil(filteredEligible.length / pageSizeStep6), prev + 1))} style={{ cursor: 'pointer' }}>
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            <div className="mt-4 d-flex justify-content-between">
              <button className="btn btn-light" onClick={() => (step === 1 ? navigate(all_routes.PostPromotion.list) : goPrev())}>
                Previous
              </button>

              <div className="d-flex align-items-center gap-2">
                {step === 3 ? (
                  <>
                    <button
                      className="btn btn-danger d-inline-flex align-items-center gap-1.5"
                      onClick={() => {
                        document.getElementById("apar-file-input")?.click();
                      }}
                    >
                      <i className="ti ti-upload fs-6"></i> Upload APAR Marks
                    </button>
                    <input
                      type="file"
                      id="apar-file-input"
                      className="d-none"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleAPARUpload}
                    />
                  </>
                ) : step < 6 ? (
                  <button className="btn btn-danger" onClick={goNext} disabled={isInitiating}>
                    {isInitiating && step === 1 ? "Initiating..." : "Next"}
                  </button>
                ) : (
                  <button className="btn btn-success" onClick={handleGenerate} disabled={isGenerating}>
                    {isGenerating ? "Generating..." : "Generate Eligible List"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showProgressModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(5px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            className="card border-0 text-center p-4 p-md-5 mx-3"
            style={{
              maxWidth: "480px",
              borderRadius: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
            }}
          >
            <div className="card-body">
              {/* Spinner */}
              <div className="d-flex justify-content-center mb-4">
                <div className="spinner-border text-danger" role="status" style={{ width: "3.5rem", height: "3.5rem", borderWidth: "4px" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>

              {/* Title & message */}
              <h5 className="fw-bold text-dark mb-2" style={{ fontSize: "18px" }}>
                {modalText}
              </h5>
              <p className="text-secondary small mb-3 px-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                It will take a few minutes to sync and calculate the data. Please do not close or refresh this page.
              </p>

              {/* Timeline Progress Bar */}
              <div className="px-3">
                <div className="progress" style={{ height: "8px", borderRadius: "4px", backgroundColor: "#e9ecef" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-danger"
                    role="progressbar"
                    style={{
                      width: `${progressPercent}%`,
                      transition: "width 0.8s linear",
                      borderRadius: "4px"
                    }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between text-muted small mt-2" style={{ fontSize: "11px" }}>
                  <span>{progressStatus}</span>
                  <span className="fw-bold text-danger">{progressPercent}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Candidate Clearance Details Modal */}
      {selectedDarCandidate && (() => {
        const modalEmpId = String(selectedDarCandidate.emp_no || selectedDarCandidate.empId || selectedDarCandidate.empNo || "");

        return (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
            onClick={() => setSelectedDarCandidate(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
              style={{ maxWidth: "560px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "12px 12px 0 0" }}>
                  <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "15px" }}>
                    <i className="ti ti-file-text text-primary fs-5"></i>
                    Candidate Clearance Details
                  </h6>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedDarCandidate(null)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body p-4">
                  {/* Candidate Info Header */}
                  <div className="p-3 rounded-3 mb-4" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div className="text-secondary fw-semibold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                          Employee Name
                        </div>
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: "17px", color: "#0f172a", lineHeight: "1.3" }}>
                          {selectedDarCandidate.emp_name || selectedDarCandidate.empName || selectedDarCandidate.employeeName || selectedDarCandidate.name || "—"}
                        </h5>
                        <div className="text-muted small mt-1.5">
                          Employee ID: <span className="fw-bold text-dark">{modalEmpId || "—"}</span>
                        </div>
                      </div>
                      {(() => {
                        const dar = getDarDetails(selectedDarCandidate);
                        return <span className={`badge ${dar.badgeClass} flex-shrink-0`} style={{ ...dar.badgeStyle, fontSize: "13px", padding: "6px 14px" }}>{dar.statusText}</span>;
                      })()}
                    </div>
                  </div>

                  {/* Applicable D&AR Fields */}
                  {(() => {
                    const dar = getDarDetails(selectedDarCandidate);
                    const rawStatus = (selectedDarCandidate.darStatus ?? selectedDarCandidate.dar_status ?? "").toString().trim();
                    const statusLower = rawStatus.toLowerCase();
                    
                    const isChargeSheet = statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued";
                    const isMinor = statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty";
                    const isMajor = statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty";
                    const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

                    return (
                      <div className="d-flex flex-column gap-3 mb-4" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Case No.</span>
                          <span className="fw-semibold text-dark">{dar.caseNo}</span>
                        </div>

                        {(isChargeSheet || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                            <span className="fw-semibold text-dark">{dar.chargeSheetIssuedDate}</span>
                          </div>
                        )}

                        {(isMinor || isMajor || isClearOrNone) && (
                          <div className="pb-2 border-bottom">
                            <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                            <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                              {dar.penaltyDescription}
                            </div>
                          </div>
                        )}

                        {(isMinor || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Date of Action</span>
                            <span className="fw-semibold text-dark">{dar.dateOfAction}</span>
                          </div>
                        )}

                        {(isMajor || isClearOrNone) && (
                          <>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty Start Date</span>
                              <span className="fw-semibold text-dark">{dar.penaltyStartDate}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pb-2">
                              <span className="text-secondary font-medium">Penalty End Date</span>
                              <span className="fw-semibold text-dark">{dar.penaltyEndDate}</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="modal-footer py-2.5 px-4 bg-light border-top d-flex justify-content-end" style={{ borderRadius: "0 0 12px 12px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4"
                    onClick={() => setSelectedDarCandidate(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default NewPromotionCycle;
